// Data model for the "Areas We Serve" section.
// Only suburbs with `published: true` render as live routes and appear in
// internal links / sitemap. Others are listed on the city hub as
// "coming soon" so we never ship thin or duplicate doorway pages.

export interface AreaFAQ {
  q: string;
  a: string;
}

export interface Suburb {
  slug: string;
  name: string;
  priority: "high" | "standard";
  published: boolean;
  metaTitle?: string;
  metaDescription?: string;
  h1?: string;
  intro?: string;
  localContext?: string;
  businessTypes?: string[];
  sections?: Array<{ heading: string; body: string }>;
  faqs?: AreaFAQ[];
  nearby?: string[]; // slugs of nearby suburbs (same city)
}

export interface City {
  slug: string;
  name: string;
  province: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
  overview: string;
  economy: string;
  businessTypes: string[];
  faqs: AreaFAQ[];
  suburbs: Suburb[];
}

// ---------------- Cape Town ----------------
const capeTownSuburbs: Suburb[] = [
  {
    slug: "cape-town-cbd",
    name: "Cape Town CBD",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Cape Town CBD | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for Cape Town CBD businesses. SAICA registered practice serving the city bowl and inner city.",
    h1: "Bookkeeping Services in Cape Town CBD",
    intro:
      "We serve businesses in the Cape Town CBD with remote bookkeeping, payroll, tax and CIPC compliance — from the professional firms clustered around Wale Street and Long Street to the retailers, agencies and hospitality operators in the city bowl.",
    localContext:
      "The Cape Town CBD is the densest commercial node in the Western Cape. Legal chambers, advocates, boutique advisory firms and financial services businesses share the city bowl with fast-growing tech companies, creative agencies, restaurants, retailers and short-stay accommodation operators. Every one of these businesses reports to the same SARS and CIPC that a manufacturer in the Northern Suburbs does — the difference is the pace, the transaction volume and the pressure on owner-managers to keep the compliance side quiet in the background.",
    sections: [
      {
        heading: "Bookkeeping built for CBD operators",
        body: "Our monthly bookkeeping for Cape Town CBD businesses covers full transaction capture from bank feeds, supplier bill processing, sales invoicing, VAT-ready reconciliations and a monthly management pack showing profit and loss, balance sheet and cash movements. Everything runs through Xero, Sage or Pastel, so there is no software you need to install and no site visit required. If you're moving off a spreadsheet, we set up the cloud file, the chart of accounts and the bank feeds as part of onboarding.",
      },
      {
        heading: "Payroll, VAT and SARS compliance",
        body: "We run monthly payroll, issue payslips, submit EMP201 by the 7th and reconcile EMP501 bi-annually. VAT201 returns are filed on the SARS cycle assigned to your business. Provisional tax (IRP6) is prepared in August and February, the ITR14 annual company return is filed after year-end, and CIPC annual returns and beneficial ownership submissions are handled so your company stays in good standing.",
      },
      {
        heading: "Working with a remote practice from the CBD",
        body: "Bank feeds pull transactions from FNB, Standard Bank, Nedbank, ABSA, Capitec and Investec straight into your accounting system. Payroll inputs, supplier invoices and any SARS correspondence come via email or a shared drive. A monthly video call walks you through the numbers before month-end close. There are no travel fees and no charge for the time it would take to drive into the CBD.",
      },
    ],
    businessTypes: [
      "Legal firms, advocates and chambers",
      "Boutique financial services and advisory firms",
      "Creative agencies, design studios and production houses",
      "Tech startups and SaaS businesses",
      "Restaurants, cafés and bars in the city bowl",
      "Retailers and specialist stores",
      "Short-stay and boutique hospitality operators",
      "Property companies and letting agents",
      "Consultants and independent professionals",
    ],
    faqs: [
      {
        q: "Do you have an office in the Cape Town CBD?",
        a: "No. Our practice is fully remote and works with CBD businesses digitally. You get a SAICA registered team without any of the parking, courier or in-person meeting costs baked into your monthly fee.",
      },
      {
        q: "Can you take over a set of books mid-year?",
        a: "Yes. We request the trial balance and supporting schedules from your current bookkeeper, verify the opening balances and continue in whichever software you already use. There is no charge to migrate between Xero, Sage or Pastel if a change makes sense.",
      },
      {
        q: "How is VAT handled for a CBD hospitality or retail business?",
        a: "Sales are reconciled from your POS or till system against bank deposits, supplier VAT is checked against valid tax invoices, and VAT201 is filed via eFiling by the 25th of the reporting month. We flag any input VAT that will not survive a SARS verification before it goes on the return.",
      },
      {
        q: "Do you support businesses on the SEZ or with foreign-currency income?",
        a: "Yes. Foreign-invoiced income, forex bank accounts and multi-currency Xero files are common in the CBD. We record the transactions correctly, apply the SARS rules on translation and make sure the VAT and income tax treatment is defensible.",
      },
    ],
    nearby: ["foreshore", "waterfront", "green-point", "sea-point"],
  },
  {
    slug: "foreshore",
    name: "Foreshore",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Foreshore Cape Town | SARS & Payroll",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for corporate and professional businesses in the Foreshore, Cape Town. SAICA registered practice.",
    h1: "Bookkeeping Services in the Foreshore, Cape Town",
    intro:
      "We serve businesses in the Foreshore precinct with remote bookkeeping, payroll, VAT and SARS compliance — supporting the head offices, professional firms and corporate tenants that occupy the towers along Heerengracht, Hertzog and Table Bay Boulevard.",
    localContext:
      "The Foreshore is Cape Town's corporate address. It houses head offices, listed-company divisions, large legal and audit firms, financial services businesses and the international-facing tenants of Portside, the Standard Bank Centre and the surrounding blocks. The businesses here typically run tight month-end cycles, need clean management accounts on time, and cannot afford a lapse on VAT, PAYE or CIPC deadlines.",
    sections: [
      {
        heading: "Month-end that closes on the calendar you agreed",
        body: "For Foreshore businesses we work to a fixed monthly close date. Bank reconciliations, supplier bills, sales invoicing and VAT-ready GL cleanup are done in the same week each month, and a management pack lands in your inbox before the executive review meeting. Xero, Sage and Pastel are all supported.",
      },
      {
        heading: "Payroll for professional teams",
        body: "We handle monthly payroll for salaried teams, process leave, bonuses and directors' remuneration, and submit EMP201 by the 7th. EMP501 bi-annual reconciliations and IRP5 issuing are included. UIF is filed monthly, and Workmen's Compensation returns are covered in the Medium Level package.",
      },
      {
        heading: "Tax, AFS and CIPC",
        body: "Provisional tax (IRP6), ITR14 annual returns and annual financial statements are prepared by a SAICA registered team. CIPC annual returns and beneficial ownership submissions are filed on time so directors and shareholders don't get caught out by a deregistration threat.",
      },
    ],
    businessTypes: [
      "Corporate head offices and divisional teams",
      "Large legal firms and audit practices",
      "Financial services and asset management businesses",
      "Property and REIT companies",
      "Import, export and shipping-related businesses",
      "Consultancies and advisory firms",
      "Serviced-office tenants and small corporate teams",
    ],
    faqs: [
      {
        q: "Can you slot into an existing month-end reporting cycle?",
        a: "Yes. We agree the close date, deliverables and format up front — most Foreshore clients want a P&L, balance sheet, cash-flow summary and variance commentary. Everything is delivered in your preferred format on the same day each month.",
      },
      {
        q: "Do you work with holding-company and subsidiary structures?",
        a: "Yes. Multiple entities under one owner are common in the Foreshore. Each entity gets its own bookkeeping file and its own SARS and CIPC compliance, and we consolidate for management reporting where required.",
      },
      {
        q: "Are your fees based on suburb or on the size of the business?",
        a: "Fees are based on transaction volume, number of employees and VAT status — never on suburb. A Foreshore corporate and a Bellville manufacturer with the same profile pay the same fee.",
      },
      {
        q: "Do you handle SARS verifications and objections?",
        a: "Yes. Our registered tax practitioners handle verifications, requests for supporting documents, objections and appeals via eFiling. Nothing needs to be dropped off at a SARS branch.",
      },
    ],
    nearby: ["cape-town-cbd", "waterfront", "green-point", "century-city"],
  },
  {
    slug: "century-city",
    name: "Century City",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Century City | Xero, Payroll & VAT",
    metaDescription:
      "Remote bookkeeping, Xero setup, payroll, VAT and SARS compliance for Century City businesses. SAICA registered team supporting tech, finance and professional firms.",
    h1: "Bookkeeping Services in Century City",
    intro:
      "We serve businesses in Century City with remote bookkeeping, payroll, VAT and SARS compliance — supporting the tech, financial services, insurance and professional firms clustered around the Bridgeways precinct, Central Park, The Estuaries and Bridge Park.",
    localContext:
      "Century City is one of the fastest-growing corporate parks in the country. It's the Cape Town base for large financial services businesses, insurers, medical schemes, tech companies and the professional firms that support them. The typical Century City client is a growing business running Xero or Sage, with a mix of local and cross-border customers and a real need for management accounts that hold up under an investor or lender review.",
    sections: [
      {
        heading: "Cloud accounting the way Century City runs",
        body: "Century City runs on Xero and Sage. We set up new files, clean up legacy files and take over from previous bookkeepers without disruption. Bank feeds from FNB, Standard Bank, Nedbank, ABSA, Capitec, Investec and Mercantile flow directly into the accounting system, and we reconcile within the week. Add-ons like Dext for supplier bills, Simplepay or SageOne Payroll and Hubdoc are supported.",
      },
      {
        heading: "Payroll and PAYE for growing teams",
        body: "Century City clients often move from a handful of staff to twenty or thirty in a year. We scale monthly payroll accordingly, submit EMP201 by the 7th, reconcile EMP501 bi-annually and issue IRP5s. UIF, SDL and Workmen's Compensation are handled without you having to think about the deadlines.",
      },
      {
        heading: "VAT, tax and AFS for tech, finance and services",
        body: "Zero-rated exports, imported-services VAT and cross-border invoicing all come up regularly at Century City. We handle VAT201 correctly, prepare IRP6 provisional tax in August and February, file ITR14 annual returns and prepare annual financial statements that satisfy SARS, lenders and any due-diligence review.",
      },
    ],
    businessTypes: [
      "Tech, SaaS and fintech businesses",
      "Financial services and asset managers",
      "Insurance brokers and medical scheme administrators",
      "Legal, tax and consulting firms",
      "Marketing agencies and digital studios",
      "Medical practices and specialist consulting rooms",
      "Property companies and letting agents",
      "Growing startups scaling headcount",
    ],
    faqs: [
      {
        q: "We run on Xero — can you keep our current file?",
        a: "Yes. We continue in your existing Xero file, tidy up the chart of accounts and reconciliations where needed, and take over month-end. There is no forced migration.",
      },
      {
        q: "Do you handle foreign-currency invoicing and forex accounts?",
        a: "Yes. We record forex bank accounts, apply the correct SARS translation rules and make sure zero-rated exports and imported services are reported properly on VAT201 and in your management accounts.",
      },
      {
        q: "Can you support a startup that is not yet VAT registered?",
        a: "Yes. Below the R1m compulsory VAT threshold you don't need to register, and we advise on when voluntary registration makes sense. The Entry Level package fits most pre-VAT startups.",
      },
      {
        q: "How do you handle due diligence or investor requests?",
        a: "We deliver management accounts, tax and VAT reconciliations, payroll summaries and CIPC records to your investors, lenders or acquirers on request. Historical clean-up work is quoted separately if required.",
      },
    ],
    nearby: ["foreshore", "cape-town-cbd", "tyger-valley", "bellville"],
  },
  {
    slug: "claremont",
    name: "Claremont",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Claremont Cape Town | SARS & Payroll",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for professional and medical practices in Claremont, Cape Town. SAICA registered practice.",
    h1: "Bookkeeping Services in Claremont, Cape Town",
    intro:
      "We serve businesses in Claremont with remote bookkeeping, payroll, VAT and SARS compliance — including the professional firms, medical and dental practices, retailers and small companies that operate along Main Road, Protea Road and the Cavendish precinct.",
    localContext:
      "Claremont is one of the busiest professional and retail hubs in the Southern Suburbs. It's home to law and accounting practices, specialist medical and dental rooms, property and letting businesses, retailers around Cavendish Square and Stadium on Main, and a large concentration of owner-managed service businesses. Most of these clients want clean monthly numbers, sensible tax planning and a compliance partner who doesn't need to be walked through the basics.",
    sections: [
      {
        heading: "Bookkeeping for professional and medical practices",
        body: "For Claremont practices we handle daily transaction capture, patient or client billing reconciliations, medical-aid receipts, supplier bills and month-end close. Trust and business accounts (where relevant) are kept separate and reconciled independently. Xero, Sage and Pastel are all supported.",
      },
      {
        heading: "Payroll for practice staff",
        body: "We run monthly payroll for practice staff, associates and locums, issue payslips, submit EMP201 monthly and reconcile EMP501 bi-annually. UIF and SDL are handled, and Workmen's Compensation returns are included in the Medium Level package.",
      },
      {
        heading: "Tax, VAT and annual compliance",
        body: "Provisional tax and ITR14 filings are prepared by a SAICA registered team, VAT201 returns go out on your SARS cycle, and annual financial statements are prepared for the practice. CIPC annual returns and beneficial ownership submissions are kept current.",
      },
    ],
    businessTypes: [
      "Medical and dental practices",
      "Specialist consulting rooms",
      "Legal and accounting firms",
      "Property companies and letting agents",
      "Retailers in Cavendish and along Main Road",
      "Restaurants, cafés and speciality food businesses",
      "Consultants and independent professionals",
      "Schools of instruction, tutoring and training businesses",
    ],
    faqs: [
      {
        q: "Do you understand the specific reporting a medical practice needs?",
        a: "Yes. Medical-aid receipts, EFT and card reconciliations, associate billing and locum payments are handled correctly, and the monthly management pack separates clinical revenue, dispensary revenue and other income where relevant.",
      },
      {
        q: "Can you handle a small law firm's trust reconciliations?",
        a: "Business-account bookkeeping, VAT, PAYE and annual financial statements are our core service. Formal LPC trust-account audit work is done by an external independent auditor, and we work alongside that process.",
      },
      {
        q: "How do we send documents to you from Claremont?",
        a: "Bank feeds, email attachments and a shared drive cover 95% of documents. There's no need to drop anything off in person, and no courier costs.",
      },
      {
        q: "Do you support both directors' salaries and drawings?",
        a: "Yes. Directors' remuneration through payroll (with PAYE) and shareholder loan movements are recorded correctly, and we advise on the mix each year around the provisional tax cycle.",
      },
    ],
    nearby: ["newlands", "constantia", "cape-town-cbd", "sea-point"],
  },
  {
    slug: "newlands",
    name: "Newlands",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Newlands Cape Town | Payroll & Tax",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for professional firms and practices in Newlands, Cape Town. SAICA registered practice.",
    h1: "Bookkeeping Services in Newlands, Cape Town",
    intro:
      "We serve businesses in Newlands with remote bookkeeping, payroll, VAT and SARS compliance — supporting the professional firms, boutique investment houses, medical practices and hospitality operators clustered along Main Road, Dean Street and around the Newlands stadium precinct.",
    localContext:
      "Newlands sits at the intersection of professional-services Cape Town and the leafier Southern Suburbs residential base. The tenant mix is heavily weighted toward legal firms, boutique wealth managers, tech and consulting businesses, and specialist medical practices. Newlands also hosts a cluster of restaurants, boutique hotels and event venues that fill up around cricket and rugby fixtures.",
    sections: [
      {
        heading: "Monthly bookkeeping and management accounts",
        body: "We capture transactions, reconcile bank accounts, process supplier bills and invoice customers on Xero, Sage or Pastel. A month-end management pack — P&L, balance sheet, cash summary — is delivered on a fixed date each month so partners and directors can review it without chasing.",
      },
      {
        heading: "Payroll for partnerships and small firms",
        body: "Salaried staff, associate payments, partners' drawings and directors' remuneration are all handled. EMP201 is filed monthly, EMP501 bi-annually, UIF is filed, and IRP5s are issued at year-end.",
      },
      {
        heading: "Tax and annual compliance",
        body: "Provisional tax, ITR14 annual returns and annual financial statements are prepared by a SAICA registered team. CIPC annual returns and beneficial ownership submissions are filed on time so the company stays in good standing.",
      },
    ],
    businessTypes: [
      "Legal firms and advocates",
      "Boutique investment and wealth-management houses",
      "Consulting and advisory firms",
      "Medical practices and specialist rooms",
      "Property companies and body corporates",
      "Restaurants, boutique hotels and event venues",
      "Owner-managed service businesses",
    ],
    faqs: [
      {
        q: "Do you work with partnerships as well as companies?",
        a: "Yes. Partnerships, incorporated professional practices, close corporations and private companies are all supported. The reporting is tailored to each structure.",
      },
      {
        q: "Can you produce quarterly partner accounts?",
        a: "Yes. On top of the monthly management pack we can produce quarterly partner or director statements showing their share of profit, drawings and closing capital account.",
      },
      {
        q: "Do you help with SARS provisional tax planning?",
        a: "Yes. We produce a projected taxable income position ahead of August and February so the IRP6 submission reflects a realistic estimate and doesn't trigger underestimation penalties.",
      },
      {
        q: "How quickly can you take over from an existing bookkeeper?",
        a: "Two to three weeks is typical. We request the trial balance and supporting schedules, verify the opening balances, and pick up the next month-end without breaking the reporting cycle.",
      },
    ],
    nearby: ["claremont", "constantia", "cape-town-cbd", "sea-point"],
  },
  {
    slug: "constantia",
    name: "Constantia",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Constantia Cape Town | Tax & Payroll",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for estates, hospitality and professional businesses in Constantia, Cape Town. SAICA registered practice.",
    h1: "Bookkeeping Services in Constantia, Cape Town",
    intro:
      "We serve businesses in Constantia with remote bookkeeping, payroll, VAT, tax and CIPC compliance — supporting wine estates, boutique hospitality operators, professional firms, medical practices and high-net-worth family-owned companies across the valley.",
    localContext:
      "Constantia has a distinctive business mix: wine estates and cellar-door operations, boutique hotels and restaurants, veterinary and equestrian businesses, medical and dental practices, and a large base of professional consultants and family-owned companies. Many of these operate as small private companies with directors who need clean books, sensible tax planning and confidence that CIPC and SARS are handled.",
    sections: [
      {
        heading: "Bookkeeping for estates and hospitality operators",
        body: "Cellar-door sales, restaurant revenue, function income, accommodation revenue and retail sales are separated in the chart of accounts, reconciled to the till or POS, and rolled into a monthly management pack. Supplier bills and stock movements are recorded on Xero, Sage or Pastel with the correct VAT treatment.",
      },
      {
        heading: "Professional and medical practices",
        body: "For legal, medical, dental and consulting practices in Constantia we handle daily bookkeeping, payroll, PAYE, VAT (where registered) and annual tax and CIPC compliance. Directors' remuneration and drawings are structured tax-efficiently in consultation with the owner.",
      },
      {
        heading: "Family-owned companies and trusts",
        body: "Where the business is owned via a trust or holding company we bookkeep each entity separately, prepare annual financial statements and file the correct SARS and CIPC returns for each. We work alongside your existing tax adviser on any trust-specific reporting.",
      },
    ],
    businessTypes: [
      "Wine estates and cellar-door operations",
      "Boutique hotels, guesthouses and restaurants",
      "Veterinary, equestrian and pet-care businesses",
      "Medical, dental and specialist practices",
      "Legal and advisory firms",
      "Property companies and letting operators",
      "Family-owned holding companies",
      "Consultants and independent professionals",
    ],
    faqs: [
      {
        q: "Can you handle a wine estate with retail, hospitality and export income?",
        a: "Yes. Retail cellar-door sales, restaurant and function income, wholesale and zero-rated export sales each get their own treatment in the books and on the VAT201. The management pack separates them clearly.",
      },
      {
        q: "Do you handle the accounts for a small boutique guesthouse?",
        a: "Yes. Nightly rate income, OTA commissions, breakfast and food-and-beverage revenue and cleaning-service costs are all handled. VAT is applied correctly and the monthly pack shows occupancy-related trends.",
      },
      {
        q: "Do you work with the owner's tax adviser or auditor?",
        a: "Yes. Where you already have a tax specialist, wealth manager or independent auditor we work alongside them and provide the trial balance, general ledger and supporting schedules they need.",
      },
      {
        q: "How do we exchange documents?",
        a: "Bank feeds cover most transactions. Supplier bills, payroll inputs and any SARS correspondence go through email or a shared drive. Nothing needs to be dropped off in person.",
      },
    ],
    nearby: ["claremont", "newlands", "sea-point", "cape-town-cbd"],
  },
  {
    slug: "sea-point",
    name: "Sea Point",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Sea Point Cape Town | Payroll & VAT",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for hospitality, retail and professional businesses in Sea Point, Cape Town. SAICA registered.",
    h1: "Bookkeeping Services in Sea Point, Cape Town",
    intro:
      "We serve businesses in Sea Point with remote bookkeeping, payroll, VAT and SARS compliance — supporting the restaurants, cafés, boutique hotels, short-stay operators, medical practices and small companies along Main Road, Regent Road and the Atlantic Seaboard promenade.",
    localContext:
      "Sea Point is dense, hospitality-heavy and one of the busiest small-business precincts in Cape Town. The typical Sea Point client is a restaurant, café, deli, boutique retailer, short-stay accommodation operator or medical practice — often owner-managed, VAT registered and running tight cash cycles. Getting VAT and PAYE right on time matters, and so does a management pack that shows what the business actually earned each month.",
    sections: [
      {
        heading: "Bookkeeping for restaurants and hospitality",
        body: "Till and POS sales are reconciled daily against bank deposits, card settlements and delivery-platform payouts. Supplier bills are captured, staff meals and complimentaries are recorded correctly, and month-end delivers a P&L that separates food, beverage and other income.",
      },
      {
        heading: "Short-stay and property operators",
        body: "Airbnb, Booking.com and direct nightly-rate revenue is recorded with the OTA commission and payment-processor fees separated so gross revenue and net take-home are both visible. VAT is applied correctly to short-stay income above the threshold, and rates, levies and cleaning costs are allocated to the correct property.",
      },
      {
        heading: "PAYE, UIF and VAT on the SARS clock",
        body: "EMP201 is filed by the 7th of each month, EMP501 bi-annually, UIF monthly, and VAT201 on your assigned SARS cycle. Late-submission penalties are avoided by working to the calendar rather than to the reminder.",
      },
    ],
    businessTypes: [
      "Restaurants, cafés and delis",
      "Boutique hotels and guesthouses",
      "Short-stay and Airbnb operators",
      "Retailers along Main Road and Regent Road",
      "Fitness, wellness and beauty businesses",
      "Medical, dental and allied-health practices",
      "Body corporates and property-management companies",
      "Consultants and small owner-managed businesses",
    ],
    faqs: [
      {
        q: "We run three restaurants under one company — can you keep them separate?",
        a: "Yes. Each venue gets its own tracking category or class in Xero or Sage so revenue and direct costs are visible per outlet, and the consolidated view still gives you a company-level P&L.",
      },
      {
        q: "Do you handle Yoco, iKhokha and card-machine reconciliations?",
        a: "Yes. Card settlements and merchant fees are reconciled against gross sales, and any variances against the till report are flagged before month-end close.",
      },
      {
        q: "Can you reconcile Airbnb and Booking.com payouts to gross revenue?",
        a: "Yes. Platform payouts arrive net of commission and fees — we reconstruct the gross booking value, record commission and payment-processing costs correctly and record the VAT position (where applicable).",
      },
      {
        q: "What if we're behind on VAT and PAYE?",
        a: "We quote separately for the catch-up period, rebuild the affected months, file the outstanding returns and negotiate any penalty and interest remissions with SARS where the facts support it.",
      },
    ],
    nearby: ["green-point", "waterfront", "cape-town-cbd", "claremont"],
  },
  {
    slug: "green-point",
    name: "Green Point",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Green Point Cape Town | Payroll & Tax",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for hospitality, agency and professional businesses in Green Point, Cape Town. SAICA registered.",
    h1: "Bookkeeping Services in Green Point, Cape Town",
    intro:
      "We serve businesses in Green Point with remote bookkeeping, payroll, VAT and SARS compliance — from the boutique hotels, restaurants and short-stay operators around Main Road and the Stadium precinct to the creative agencies and small companies operating out of Somerset Road and De Waterkant.",
    localContext:
      "Green Point sits between the V&A Waterfront and Sea Point and shares characteristics with both. The business mix is heavy in hospitality, short-stay accommodation, restaurants and small retail, plus a well-established cluster of creative agencies, studios and tech businesses in and around De Waterkant.",
    sections: [
      {
        heading: "Hospitality and short-stay bookkeeping",
        body: "For guesthouses, boutique hotels and short-stay operators we reconcile OTA payouts (Airbnb, Booking.com, Expedia) to gross revenue, separate commission and fees, allocate rates, levies and cleaning costs per property and deliver a monthly view that shows revenue per available room and gross operating profit.",
      },
      {
        heading: "Agencies, studios and small companies",
        body: "For agency and studio clients we handle project-based billing, retainer income, freelancer and supplier payments, and monthly management accounts on Xero, Sage or Pastel. Retainer income is deferred and released correctly so the monthly P&L reflects work delivered, not just cash received.",
      },
      {
        heading: "PAYE, VAT and annual compliance",
        body: "Monthly payroll, EMP201, EMP501 bi-annual reconciliations, UIF and VAT201 are handled on the SARS cycle. Provisional tax, ITR14 annual returns, annual financial statements and CIPC annual returns are all prepared and filed by a SAICA registered team.",
      },
    ],
    businessTypes: [
      "Boutique hotels and guesthouses",
      "Short-stay and Airbnb operators",
      "Restaurants and event venues",
      "Creative agencies, studios and production houses",
      "Tech and SaaS startups",
      "Retailers around Main Road and De Waterkant",
      "Fitness, wellness and beauty businesses",
      "Small owner-managed service companies",
    ],
    faqs: [
      {
        q: "Can you manage separate books per property for a short-stay operator?",
        a: "Yes. Each unit gets its own tracking category, so revenue, costs, rates and levies are visible per property and per company as a whole.",
      },
      {
        q: "Do you handle retainer income for agencies?",
        a: "Yes. Retainer invoices are recorded as deferred revenue and released monthly against the work delivered, which gives you a fair reflection of monthly performance rather than lumpy cash-based income.",
      },
      {
        q: "How does VAT work for us if we invoice international clients?",
        a: "Services exported to non-residents are usually zero-rated for VAT, provided the SARS documentation requirements are met. We record the invoices correctly and keep the supporting evidence for a verification.",
      },
      {
        q: "Do you help with SARS registrations for a new company?",
        a: "Yes. Income tax, PAYE, UIF, SDL and VAT registrations are all handled on eFiling, along with CIPC and beneficial ownership registration where required.",
      },
    ],
    nearby: ["sea-point", "waterfront", "cape-town-cbd", "foreshore"],
  },
  {
    slug: "waterfront",
    name: "V&A Waterfront",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services V&A Waterfront | Payroll & SARS",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for retail, hospitality and corporate tenants at the V&A Waterfront, Cape Town. SAICA registered.",
    h1: "Bookkeeping Services at the V&A Waterfront",
    intro:
      "We serve businesses trading at the V&A Waterfront with remote bookkeeping, payroll, VAT and SARS compliance — supporting retailers, restaurants, hospitality operators, tour operators and corporate tenants across the Alfred and Victoria basins, the Silo District and the Clock Tower precinct.",
    localContext:
      "The V&A Waterfront is one of the highest-turnover retail precincts in South Africa. The businesses trading there are typically VAT registered, run POS systems with high daily transaction counts, deal in multiple currencies (rand, USD, GBP and EUR from tourist trade) and face landlord reporting obligations on top of SARS and CIPC.",
    sections: [
      {
        heading: "Daily reconciliations for high-volume retail",
        body: "For Waterfront retailers we reconcile daily till, POS and card takings to bank deposits and merchant settlements, separate VAT-inclusive sales from zero-rated tourist refund sales where applicable, and produce a clean monthly gross-margin view.",
      },
      {
        heading: "Restaurants, bars and hospitality",
        body: "Food, beverage and service income is separated, supplier bills are captured with correct VAT, staff meals and complimentaries are recorded properly and the monthly management pack shows outlet-level performance if you operate more than one venue.",
      },
      {
        heading: "PAYE, VAT and tenant reporting",
        body: "Monthly payroll, EMP201, EMP501 bi-annual reconciliations, UIF and VAT201 are handled on the SARS cycle. Where landlord turnover-rent calculations are required we produce the certified turnover figure alongside the management accounts.",
      },
    ],
    businessTypes: [
      "Retailers in the Victoria Wharf and Watershed",
      "Restaurants, bars and quick-service outlets",
      "Boutique hotels and hospitality operators",
      "Tour operators and experience businesses",
      "Marine and charter businesses",
      "Corporate tenants in the Silo District and Portside",
      "Galleries and speciality retailers",
    ],
    faqs: [
      {
        q: "Can you produce the certified turnover figure our landlord requires?",
        a: "Yes. We produce a monthly certified turnover figure from the reconciled sales ledger for landlord reporting, alongside your management accounts.",
      },
      {
        q: "We take payments in multiple currencies — how is that handled?",
        a: "Multi-currency takings are recorded at the correct SARS rate, forex bank accounts are reconciled at month-end and any exchange gains or losses are recognised in the P&L.",
      },
      {
        q: "Do you handle tourist tax-refund sales?",
        a: "Yes. Sales made to qualifying non-resident tourists are recorded separately so the VAT refund process runs cleanly and the VAT201 is filed correctly.",
      },
      {
        q: "Do you support businesses that pay commission to tour agents or OTAs?",
        a: "Yes. Commission and agent fees are recorded against the relevant revenue lines, so net take-home per booking is visible and the VAT treatment is correct.",
      },
    ],
    nearby: ["green-point", "cape-town-cbd", "foreshore", "sea-point"],
  },
  {
    slug: "tyger-valley",
    name: "Tyger Valley",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Tyger Valley | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for corporate, financial and professional businesses in the Tyger Valley, Cape Town. SAICA registered.",
    h1: "Bookkeeping Services in Tyger Valley",
    intro:
      "We serve businesses in the Tyger Valley corporate corridor with remote bookkeeping, payroll, VAT and SARS compliance — supporting the financial services businesses, insurance operators, medical practices and professional firms based around Willie van Schoor Avenue, Bill Bezuidenhout Avenue and the Tyger Waterfront.",
    localContext:
      "Tyger Valley is the Northern Suburbs' equivalent of Sandton or Century City: a dense corporate corridor with head offices of financial services and insurance businesses, large professional firms, medical and specialist practices and a growing tech presence. Many businesses here have multiple entities, VAT registration, sizeable payrolls and a strong preference for tight monthly reporting.",
    sections: [
      {
        heading: "Bookkeeping for financial and professional businesses",
        body: "Multi-entity structures, intercompany accounts and consolidated management reporting are handled properly. Bank feeds, supplier bills and sales invoicing run through Xero, Sage or Pastel and month-end delivers a management pack in the format the executive team wants.",
      },
      {
        heading: "Payroll and PAYE at scale",
        body: "We run payroll for teams of five to fifty, submit EMP201 monthly and reconcile EMP501 bi-annually. Directors' remuneration, bonuses, medical-aid and retirement-fund deductions are structured correctly, and IRP5s are issued at year-end.",
      },
      {
        heading: "VAT, tax and CIPC",
        body: "VAT201 on the SARS cycle, IRP6 provisional tax in August and February, ITR14 annual returns, annual financial statements and CIPC annual returns are all prepared and filed. Beneficial ownership submissions are kept current.",
      },
    ],
    businessTypes: [
      "Financial services and asset managers",
      "Insurance brokerages and short-term insurers",
      "Medical, dental and specialist practices",
      "Legal and audit firms",
      "Consulting and advisory businesses",
      "Property companies and body corporates",
      "Tech and back-office operations",
      "Owner-managed companies scaling headcount",
    ],
    faqs: [
      {
        q: "Do you handle multi-entity structures with intercompany balances?",
        a: "Yes. Each entity gets its own bookkeeping file. Intercompany balances are reconciled monthly and any consolidations for management reporting are produced separately.",
      },
      {
        q: "Can you match our internal month-end deadline?",
        a: "Yes. Where you have a fixed close date we work to it — bank reconciliations, supplier bills and month-end journals are all completed to hit the deadline.",
      },
      {
        q: "Do you support businesses on the FSCA reporting framework?",
        a: "Financial services businesses often have FSCA-specific reporting on top of SARS and CIPC. We provide the underlying management accounts, ledgers and payroll data your compliance officer or external adviser needs.",
      },
      {
        q: "How do you handle onboarding for a larger business?",
        a: "Onboarding takes two to three weeks. We collect the trial balance, verify opening balances, take over the software, set up the bank feeds and continue from the following month-end without disruption.",
      },
    ],
    nearby: ["durbanville", "bellville", "century-city", "foreshore"],
  },
  {
    slug: "durbanville",
    name: "Durbanville",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Durbanville | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for businesses in Durbanville, Cape Town. SAICA registered team supporting SMEs, practices and estates.",
    h1: "Bookkeeping Services in Durbanville",
    intro:
      "We serve businesses in Durbanville with remote bookkeeping, payroll, VAT, tax and CIPC compliance — supporting owner-managed SMEs, medical and dental practices, wine estates, professional firms and property businesses across the town.",
    localContext:
      "Durbanville is a strong owner-managed business area. The typical client is a family-owned company, professional practice, wine estate or property business that has grown to the point where spreadsheet bookkeeping and a part-time bookkeeper no longer cut it. Clean monthly numbers, sensible tax and a partner who handles SARS and CIPC quietly in the background are what matters.",
    sections: [
      {
        heading: "Monthly bookkeeping and management accounts",
        body: "We run monthly bookkeeping on Xero, Sage or Pastel, reconcile bank accounts, process supplier bills and deliver a management pack showing profit, balance sheet and cash movements. New businesses get a fresh file set up; established businesses can continue in their existing file.",
      },
      {
        heading: "Payroll for practice and family-owned teams",
        body: "Salaried staff, hourly staff, directors' remuneration and shareholder drawings are all handled correctly. EMP201, EMP501, UIF and IRP5 issuing are included.",
      },
      {
        heading: "Wine estates, hospitality and retail",
        body: "For estates, restaurants and retailers we separate revenue streams (cellar-door, wholesale, export, restaurant, function, retail) and apply the correct VAT treatment. Stock, cost of sales and gross margin are visible each month.",
      },
    ],
    businessTypes: [
      "Owner-managed SMEs and family companies",
      "Medical, dental and specialist practices",
      "Wine estates and cellar-door operations",
      "Legal and accounting firms",
      "Property companies and letting agents",
      "Restaurants, cafés and boutique retailers",
      "Contractors, tradespeople and specialist services",
      "Consultants and independent professionals",
    ],
    faqs: [
      {
        q: "We've been running on spreadsheets — can you set up Xero for us?",
        a: "Yes. New-client setup on Xero, Sage or Pastel is part of onboarding, along with chart of accounts, bank feeds and opening balances. Ongoing monthly bookkeeping starts from the following month.",
      },
      {
        q: "Do you handle catch-up bookkeeping for prior years?",
        a: "Yes. Catch-up work is quoted separately based on transaction volume and complexity. We rebuild the affected years, file the outstanding SARS returns and bring CIPC annual returns up to date.",
      },
      {
        q: "Can you take over PAYE and UIF from our current payroll provider?",
        a: "Yes. We take over the payroll file, set up eFiling access and continue submitting EMP201, EMP501 and UIF without a break in the cycle.",
      },
      {
        q: "How much does monthly bookkeeping cost for a Durbanville business?",
        a: "Fees depend on size and complexity: the Basic package is R300 a month, Entry Level (under R2.3m turnover, up to 5 employees) is R2,500, and Medium Level (under R12m turnover, up to 10 employees, VAT) is R4,500.",
      },
    ],
    nearby: ["tyger-valley", "bellville", "century-city", "stellenbosch"],
  },
  {
    slug: "bellville",
    name: "Bellville",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Bellville Cape Town | Payroll & VAT",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for SMEs, retailers and light industrial businesses in Bellville, Cape Town. SAICA registered.",
    h1: "Bookkeeping Services in Bellville, Cape Town",
    intro:
      "We serve businesses in Bellville with remote bookkeeping, payroll, VAT and SARS compliance — supporting the SMEs, retailers, medical practices, motor and light industrial businesses that operate along Voortrekker Road, Durban Road and Willie van Schoor Avenue.",
    localContext:
      "Bellville is a broad commercial and light industrial area. The business mix runs from established retailers and franchisees along Voortrekker Road and Willowbridge, through motor and automotive-related businesses, to manufacturers, wholesalers and distributors operating out of the industrial parks. Owner-managed companies dominate.",
    sections: [
      {
        heading: "Bookkeeping for SMEs and industrial operators",
        body: "Stock movements, supplier bills, sales invoicing and bank reconciliations run through Xero, Sage or Pastel. Month-end delivers a management pack that shows gross margin, operating cost and profit — the numbers owner-managers actually use to run the business.",
      },
      {
        heading: "Payroll, PAYE and Workmen's Compensation",
        body: "Wage and salaried payroll are both supported. EMP201 is filed monthly, EMP501 bi-annually, UIF monthly, and Workmen's Compensation returns are covered in the Medium Level package — important for light industrial and motor businesses.",
      },
      {
        heading: "VAT, tax and CIPC",
        body: "VAT201 is filed on the SARS cycle, IRP6 provisional tax is prepared in August and February, ITR14 annual returns and annual financial statements are done at year-end, and CIPC annual returns and beneficial ownership submissions are kept current.",
      },
    ],
    businessTypes: [
      "Retailers and franchisees",
      "Motor, panel-beating and automotive businesses",
      "Wholesalers and distributors",
      "Manufacturers and light industrial operators",
      "Medical and specialist practices",
      "Contractors, tradespeople and specialist services",
      "Family-owned SMEs",
    ],
    faqs: [
      {
        q: "Do you handle stock in the books?",
        a: "Yes. Where the business runs a proper stock system we integrate opening stock, purchases, cost of sales and closing stock into the monthly numbers, so gross margin is visible each month.",
      },
      {
        q: "Can you handle Workmen's Compensation returns?",
        a: "Yes. The annual W.As.8 Return of Earnings is included in the Medium Level package, and we assist with letters of good standing when your clients require them.",
      },
      {
        q: "Do you support Afrikaans-language correspondence?",
        a: "Yes. Documentation, SARS correspondence and monthly reporting can be delivered in Afrikaans or English at your preference.",
      },
      {
        q: "Do your fees change if I'm based in Bellville versus the CBD?",
        a: "No. Fees are set by business size and complexity, not by suburb.",
      },
    ],
    nearby: ["durbanville", "tyger-valley", "century-city", "stellenbosch"],
  },
  {
    slug: "somerset-west",
    name: "Somerset West",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Somerset West | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for businesses in Somerset West and Helderberg. SAICA registered practice for SMEs and practices.",
    h1: "Bookkeeping Services in Somerset West",
    intro:
      "We serve businesses in Somerset West and the wider Helderberg basin with remote bookkeeping, payroll, VAT, tax and CIPC compliance — supporting professional firms, medical and dental practices, wine estates, hospitality operators and property companies across the area.",
    localContext:
      "Somerset West is the commercial heart of the Helderberg. Business is a mix of professional services, medical practices, retail, tourism, wine and property. Many owner-managed companies here have long-standing family shareholding structures and need clean books, proper tax and CIPC compliance and a partner who won't disappear at year-end.",
    sections: [
      {
        heading: "Bookkeeping for Helderberg SMEs",
        body: "Monthly bookkeeping on Xero, Sage or Pastel, bank reconciliations, supplier bill capture, sales invoicing and a management pack delivered on a fixed monthly date. New setups and takeovers are both handled without disruption.",
      },
      {
        heading: "Payroll and PAYE",
        body: "Monthly payroll, EMP201 by the 7th, EMP501 bi-annual reconciliations, UIF and IRP5 issuing. Directors' remuneration is structured tax-efficiently around the annual provisional tax cycle.",
      },
      {
        heading: "Wine, hospitality and tourism operators",
        body: "Cellar-door and export sales, restaurant and function income, guesthouse and short-stay revenue each get the correct VAT treatment, and the management pack separates them so gross margin per stream is visible.",
      },
    ],
    businessTypes: [
      "Professional service firms",
      "Medical, dental and specialist practices",
      "Wine estates and cellar-door operations",
      "Guesthouses, boutique hotels and restaurants",
      "Property companies and letting agents",
      "Retailers and boutique speciality shops",
      "Contractors and construction businesses",
      "Owner-managed SMEs",
    ],
    faqs: [
      {
        q: "Can you take over from a retiring local bookkeeper?",
        a: "Yes. This is a common request in the Helderberg. We work through the handover with the outgoing bookkeeper, verify the opening balances and continue the following month without a gap.",
      },
      {
        q: "Do you help with SARS provisional tax and year-end planning?",
        a: "Yes. Ahead of August and February we produce a projected taxable income position so the IRP6 is realistic and directors' remuneration is set at a level that suits the shareholders.",
      },
      {
        q: "Do you work in both English and Afrikaans?",
        a: "Yes. Reporting and correspondence can be delivered in either language at your preference.",
      },
      {
        q: "How do we send documents to you from Somerset West?",
        a: "Bank feeds, email and a shared drive cover everything. No in-person meetings or courier costs are needed for month-end.",
      },
    ],
    nearby: ["stellenbosch", "durbanville", "tyger-valley", "constantia"],
  },
  {
    slug: "stellenbosch",
    name: "Stellenbosch",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Stellenbosch | Payroll, VAT & Tax",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for tech, wine, agri and professional businesses in Stellenbosch. SAICA registered practice.",
    h1: "Bookkeeping Services in Stellenbosch",
    intro:
      "We serve businesses in Stellenbosch with remote bookkeeping, payroll, VAT, tax and CIPC compliance — supporting the tech companies, wine estates, agri businesses, professional firms and startups clustered around the town centre, Techno Park and the surrounding wine routes.",
    localContext:
      "Stellenbosch has an unusual business mix. Tech and venture-backed companies cluster around Techno Park and the Innovus precinct, wine estates and agri businesses dominate the surrounding valleys, and professional firms, boutique advisory houses and family holding companies operate out of the town centre. Each of these client types needs a different treatment in the books, and the compliance load is real.",
    sections: [
      {
        heading: "Tech and startup bookkeeping",
        body: "For tech and venture-backed businesses we run Xero-based bookkeeping with clear revenue recognition, deferred income for annual contracts, correct treatment of stock options (as a memorandum) and management accounts that survive an investor or acquirer review.",
      },
      {
        heading: "Wine estates and agri businesses",
        body: "Cellar-door sales, wholesale, zero-rated exports and restaurant income are each recorded and reported correctly. Agri businesses' Diesel Rebate claims, harvest-related seasonal cash flow and stock movements are handled in the monthly cycle.",
      },
      {
        heading: "Professional firms and holding companies",
        body: "Legal, tax, advisory and boutique investment houses are supported with monthly bookkeeping, payroll, VAT, provisional and annual tax and annual financial statements. Multi-entity holding structures are bookkept per entity and consolidated for management reporting where required.",
      },
    ],
    businessTypes: [
      "Tech, SaaS and venture-backed startups",
      "Wine estates and cellar-door operations",
      "Agri businesses, packhouses and exporters",
      "Legal, tax and advisory firms",
      "Boutique investment and wealth-management houses",
      "Restaurants, boutique hotels and guesthouses",
      "Property and family-owned holding companies",
      "Owner-managed SMEs",
    ],
    faqs: [
      {
        q: "Do you support venture-backed startups with investor reporting?",
        a: "Yes. Monthly management accounts, cash runway, revenue and burn are produced in a format that suits investor updates and board packs, on top of the standard SARS and CIPC compliance.",
      },
      {
        q: "Can you handle zero-rated wine and agri exports on VAT201?",
        a: "Yes. Export sales are recorded at zero-rate with the supporting documentation (bill of lading, customs release) retained for SARS verification, and VAT201 is filed correctly on the cycle.",
      },
      {
        q: "Do you work alongside our audit firm?",
        a: "Yes. Where an external auditor prepares the annual financial statements or audits them we prepare the trial balance, supporting schedules and management letters they need.",
      },
      {
        q: "Do you support both English and Afrikaans reporting?",
        a: "Yes. Reporting and correspondence can be delivered in either language at your preference.",
      },
    ],
    nearby: ["somerset-west", "durbanville", "constantia", "bellville"],
  },
];

// ---------------- Johannesburg ----------------
const jhbSuburbs: Suburb[] = [
  {
    slug: "sandton",
    name: "Sandton",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Sandton | Payroll, VAT & SARS Support",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for Sandton businesses. SAICA registered practice serving the Sandton CBD, Sandown and Benmore corridor.",
    h1: "Bookkeeping Services in Sandton",
    intro:
      "We serve businesses in Sandton with remote bookkeeping, payroll, VAT and SARS compliance — from the consulting firms, asset managers and law practices around Sandton City and Alice Lane through the owner-managed businesses tucked into Sandown, Benmore and Wierda Valley.",
    localContext:
      "Sandton is South Africa's densest commercial node. Multinationals, listed-company head offices, financial services businesses, legal and consulting firms, and thousands of owner-managed SMEs share the postal codes around Sandton City, Nelson Mandela Square and the Gautrain station. What every one of them has in common is the same monthly reporting rhythm to SARS and CIPC — VAT201, EMP201, EMP501, IRP6 and ITR14 do not care whether you are a global consultancy or a two-director boutique.",
    sections: [
      {
        heading: "Bookkeeping for Sandton's professional economy",
        body: "Monthly bookkeeping runs on Xero, Sage or Pastel. Bank feeds from FNB, Standard Bank, Nedbank, ABSA, Investec and Rand Merchant pull straight into your file. We reconcile the bank, capture supplier bills, raise sales invoicing where needed, clean the general ledger for VAT and produce a management pack — profit and loss, balance sheet, cash movements — before your board or exco meeting.",
      },
      {
        heading: "Payroll, VAT and tax handled remotely",
        body: "Monthly payroll, EMP201 by the 7th and EMP501 bi-annual reconciliations are standard. VAT201 is filed on your assigned SARS cycle by the 25th. Provisional tax (IRP6) is prepared in August and February, ITR14 annual company returns are filed after year-end, and CIPC annual returns and beneficial ownership submissions are kept current so your directors do not get caught out.",
      },
      {
        heading: "Why a remote practice works in Sandton",
        body: "A Sandton to Randburg meeting is a half-morning. Removing in-person visits from the workflow means your fee is not padded with travel time. SARS eFiling, CIPC eServices and cloud accounting are national — physical proximity adds no value and plenty of cost.",
      },
    ],
    businessTypes: [
      "Management consultancies and advisory firms",
      "Asset managers, fund administrators and boutique financial services",
      "Corporate law firms and tax advisors",
      "Property companies, REITs and letting agents",
      "Tech, SaaS and digital agencies",
      "Recruitment, HR and executive search firms",
      "Serviced-office tenants and small corporate teams",
      "Restaurants and retailers in Sandton City and Nelson Mandela Square",
    ],
    faqs: [
      {
        q: "Do you have an office in Sandton?",
        a: "No. Our practice is fully remote. It means Sandton clients get a SAICA registered team without paying for Sandton office overheads baked into their monthly fee.",
      },
      {
        q: "Can you support a Sandton business with multiple entities under one owner?",
        a: "Yes. Holding companies, operating subsidiaries and property-owning entities are common in Sandton. Each entity gets its own bookkeeping file, its own SARS and CIPC compliance, and consolidated management reporting where required.",
      },
      {
        q: "Do you handle SARS verifications and objections?",
        a: "Yes. Registered tax practitioners on the team deal with VAT and income tax verifications, requests for supporting documents, objections and appeals via eFiling. Nothing needs to be walked into a SARS branch.",
      },
      {
        q: "Can you work with our external auditor?",
        a: "Yes. Where a Sandton client is audited we prepare the trial balance, lead schedules and supporting documents the audit team asks for, and act as the day-to-day finance function between audits.",
      },
    ],
    nearby: ["rosebank", "bryanston", "rivonia", "melrose-arch", "illovo"],
  },
  {
    slug: "rosebank",
    name: "Rosebank",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Rosebank | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Rosebank businesses. SAICA registered practice serving the Rosebank CBD, Oxford Road and The Zone.",
    h1: "Bookkeeping Services in Rosebank",
    intro:
      "We serve businesses in Rosebank with remote bookkeeping, payroll, VAT and SARS compliance — from the professional firms and tech companies along Oxford Road through the retailers, restaurants and creative agencies clustered around The Zone and Rosebank Mall.",
    localContext:
      "Rosebank has quietly become one of Johannesburg's most active commercial nodes. Advertising and media agencies, PR firms, mid-tier legal practices, financial services businesses and a strong cluster of tech companies fill the towers along Oxford Road. Ground level is dominated by retail, restaurants and hospitality servicing the office population and the Gautrain foot traffic.",
    sections: [
      {
        heading: "Monthly bookkeeping for Rosebank agencies and firms",
        body: "We work in Xero, Sage and Pastel. Bank feeds, supplier bill processing, project or client billing, VAT-ready reconciliations and a monthly management pack are all part of the standard cycle. For agencies and consultancies we can layer in job or project reporting so you can see gross margin by client.",
      },
      {
        heading: "Payroll, VAT and SARS compliance",
        body: "Monthly payroll processing, EMP201 submissions by the 7th, EMP501 bi-annual reconciliations and IRP5 issuing are included. VAT201 is filed by the 25th. Provisional tax (IRP6), ITR14 annual returns, annual financial statements, CIPC annual returns and beneficial ownership submissions are all handled centrally.",
      },
      {
        heading: "Handovers from another Rosebank bookkeeper",
        body: "If you're moving from another bookkeeper we take the handover — trial balance, supporting schedules, VAT and payroll history — verify the opening balances and pick up in the same software. There is no charge to migrate between Xero, Sage and Pastel if it makes sense.",
      },
    ],
    businessTypes: [
      "Advertising, PR and media agencies",
      "Tech companies and SaaS businesses",
      "Boutique legal and advisory firms",
      "Property, developer and estate agencies",
      "Retailers, restaurants and hospitality at The Zone and Rosebank Mall",
      "Creative studios, production houses and photographers",
      "NPOs and section 21 companies",
      "Startups and holding companies",
    ],
    faqs: [
      {
        q: "Do you support agency job or project reporting?",
        a: "Yes. In Xero we use tracking categories, and in Sage we use projects, to give you monthly gross margin by client or campaign — useful when Rosebank agencies price against team utilisation.",
      },
      {
        q: "Can you handle NPO and section 21 bookkeeping and reporting?",
        a: "Yes. NPO bookkeeping, IT12EI annual returns, section 18A donor reporting and CIPC compliance for section 21 (NPC) companies are all supported.",
      },
      {
        q: "How is document flow handled remotely?",
        a: "Bank feeds pull transactions automatically, supplier invoices are forwarded to a dedicated inbox that captures into your accounting file, and payroll inputs come via email or a shared folder each month.",
      },
      {
        q: "Do you charge extra for Rosebank clients?",
        a: "No. Fees are set by size and complexity of the business — turnover, transaction volume, employees and VAT status — not by suburb.",
      },
    ],
    nearby: ["sandton", "melrose-arch", "illovo", "parkhurst", "houghton"],
  },
  {
    slug: "bryanston",
    name: "Bryanston",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Bryanston | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Bryanston businesses. SAICA registered practice serving Nicolway, Epsom Downs and the William Nicol corridor.",
    h1: "Bookkeeping Services in Bryanston",
    intro:
      "We serve businesses in Bryanston with remote bookkeeping, payroll, VAT and SARS compliance — supporting the consulting firms, medical practices, family businesses and owner-managed SMEs that operate around Nicolway, Epsom Downs and along William Nicol Drive.",
    localContext:
      "Bryanston is a suburb built around low-density office parks and neighbourhood retail. It hosts a large number of privately owned consulting and advisory businesses, medical and dental practices, IT and telecoms companies, and the head offices of family businesses that have outgrown the suburbs and prefer Bryanston to the density of Sandton CBD.",
    sections: [
      {
        heading: "Bookkeeping suited to owner-managed businesses",
        body: "Bryanston clients are typically owner-managed with tight fee sensitivity and a real need for numbers that inform decisions. Monthly bookkeeping in Xero, Sage or Pastel produces a management pack you can actually read: profit and loss, balance sheet, cash movements, and a short set of ratios that matter for your business.",
      },
      {
        heading: "Payroll and SARS for professional practices",
        body: "Payroll for salaried teams, EMP201 by the 7th, EMP501 bi-annually and IRP5 issuing are standard. VAT201, IRP6 provisional tax and ITR14 annual returns are prepared by a SAICA registered team. CIPC annual returns and beneficial ownership submissions are filed on time.",
      },
      {
        heading: "Catch-up bookkeeping and SARS remediation",
        body: "A significant portion of Bryanston enquiries are from businesses whose previous bookkeeper stopped submitting. We quote catch-up separately, rebuild the accounts, file the outstanding VAT201s, EMP201s, IRP6s and ITR14s, and bring CIPC back into good standing.",
      },
    ],
    businessTypes: [
      "Consulting and advisory firms",
      "Medical, dental and specialist practices",
      "IT and telecommunications businesses",
      "Family-owned businesses and holding companies",
      "Property companies and letting agents",
      "Engineers, architects and design practices",
      "Contractors and construction companies",
      "Neighbourhood retailers and restaurants",
    ],
    faqs: [
      {
        q: "How do you handle a doctor or specialist practice?",
        a: "Medical practices have specific quirks — practice management software revenue, medical aid receipts, RA and pension deductions, and separate directors' accounts. We reconcile the practice software revenue against bank receipts monthly and separate personal from business expenses.",
      },
      {
        q: "Can you clean up years of missed SARS submissions?",
        a: "Yes. We quote the catch-up as a one-off, rebuild the accounts, submit outstanding VAT201s, EMP201s and ITR14s, and apply for penalty remittance where reasonable grounds exist.",
      },
      {
        q: "Do you support businesses with foreign shareholders or foreign income?",
        a: "Yes. Foreign shareholding, dividend withholding tax, transfer pricing basics and forex-denominated invoicing are all handled. Where a foreign parent needs reporting in a specific format we accommodate it.",
      },
      {
        q: "Which package suits a typical Bryanston practice?",
        a: "Most owner-managed Bryanston businesses fit the Entry Level (turnover under R2.3m, up to 5 staff) or Medium Level (turnover under R12m, up to 10 staff plus VAT). Larger practices are quoted a bespoke monthly retainer.",
      },
    ],
    nearby: ["sandton", "fourways", "rivonia", "randburg", "woodmead"],
  },
  {
    slug: "midrand",
    name: "Midrand",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Midrand | Payroll, VAT & SARS Compliance",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Midrand businesses. SAICA registered practice serving Halfway House, Kyalami, Vorna Valley and the N1 corridor.",
    h1: "Bookkeeping Services in Midrand",
    intro:
      "We serve businesses in Midrand with remote bookkeeping, payroll, VAT and SARS compliance — from the logistics operators along the N1 through the tech and telecoms tenants of Halfway House, Kyalami and Vorna Valley, and the light industrial businesses around Corporate Park South.",
    localContext:
      "Midrand sits at the heart of the Gauteng logistics and tech corridor. Data centres, telecoms head offices, logistics and courier businesses, motor dealerships and light manufacturers cluster along Old Pretoria Road, New Road and the N1. Owner-managed businesses in Vorna Valley, Halfway House and Kyalami round out the mix.",
    sections: [
      {
        heading: "Bookkeeping for logistics, light industrial and tech",
        body: "For logistics and light industrial businesses we handle high supplier and creditor volume, fleet cost tracking and job or route costing where required. Tech businesses get standard SaaS bookkeeping — deferred revenue tracking, foreign-currency processor reconciliations and multi-currency Xero files.",
      },
      {
        heading: "Payroll and BCEA compliance",
        body: "Monthly payroll for salaried and wage teams, EMP201 by the 7th, EMP501 bi-annual reconciliations, UIF filings and Workmen's Compensation returns (in Medium Level packages) are all handled. Wage payroll with overtime, allowances and shift differentials is common in Midrand and fully supported.",
      },
      {
        heading: "VAT, tax and CIPC",
        body: "VAT201 on your SARS cycle, IRP6 provisional tax in August and February, ITR14 annual returns and CIPC annual returns and beneficial ownership are all filed centrally. Where VAT audits arise from high input-VAT positions (common in capital-intensive industries) we handle the SARS verification and supply the supporting invoices.",
      },
    ],
    businessTypes: [
      "Logistics, courier and freight businesses",
      "Light manufacturing and assembly",
      "Data centres and telecoms",
      "Motor dealerships and fleet businesses",
      "Tech and SaaS companies",
      "Warehousing and distribution",
      "Property, developer and letting businesses",
      "Owner-managed SMEs in Vorna Valley, Halfway House and Kyalami",
    ],
    faqs: [
      {
        q: "Can you handle wage payroll with overtime and shift allowances?",
        a: "Yes. Weekly, fortnightly or monthly wage payroll with overtime, shift, standby and travel allowances, plus UIF, SDL and PAYE, is standard.",
      },
      {
        q: "Do you support fleet or vehicle cost tracking?",
        a: "Yes. We can set up tracking categories or classes so fuel, maintenance, tyres, licences and toll costs are visible per vehicle or per route, and reconciled against fleet card statements.",
      },
      {
        q: "How do you handle high-volume creditor processing?",
        a: "Supplier invoices are captured through an inbox that pushes them into your accounting file with data-extraction. Approvals are done inside the accounting software or the linked bill-payment tool.",
      },
      {
        q: "Do you deal with SARS VAT verifications for high input-VAT businesses?",
        a: "Yes. Import-VAT audit trails, valid tax invoice checks and supporting document submissions via eFiling are handled by registered tax practitioners.",
      },
    ],
    nearby: ["waterfall", "sunninghill", "fourways", "woodmead", "sandton"],
  },
  {
    slug: "waterfall",
    name: "Waterfall",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Waterfall City | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Waterfall City and Waterfall Business Estate businesses. SAICA registered practice on the N1 corridor.",
    h1: "Bookkeeping Services in Waterfall",
    intro:
      "We serve businesses in Waterfall City, Waterfall Business Estate and the surrounding precincts with remote bookkeeping, payroll, VAT and SARS compliance — from head offices in Mall of Africa's business towers to the logistics, distribution and light industrial tenants across the estate.",
    localContext:
      "Waterfall has become one of Gauteng's fastest-growing commercial nodes. Corporate head offices, insurers, banks and multinational-facing consultancies fill Waterfall City, while distribution, warehousing and light industrial businesses occupy the estate. The precinct's growth attracts a steady flow of owner-managed businesses — professional services, tech, engineering — that need a compliance partner without corporate overheads.",
    sections: [
      {
        heading: "Bookkeeping for corporate divisions and SMEs",
        body: "We work with divisional finance teams that need a set of books maintained monthly and reported into a group ledger, and with owner-managed SMEs that need a full outsourced finance function. Xero, Sage, Pastel and QuickBooks are all supported, and we can integrate with group reporting formats where required.",
      },
      {
        heading: "Payroll, VAT and tax on a fixed cycle",
        body: "Monthly payroll, EMP201, EMP501, UIF, VAT201, IRP6, ITR14, CIPC annual returns and beneficial ownership submissions are all delivered on a fixed monthly cycle. Deadlines are managed centrally so nothing slips through.",
      },
      {
        heading: "Group and multi-entity clients",
        body: "Waterfall is home to a lot of multi-entity structures — operating companies, holding companies, property-owning entities and share-incentive vehicles. Each entity gets its own bookkeeping file and compliance, with consolidated reporting where the owner or group needs it.",
      },
    ],
    businessTypes: [
      "Corporate divisions and head offices in Waterfall City",
      "Insurers and financial services businesses",
      "Warehousing, distribution and 3PL operators",
      "Light manufacturers and assemblers",
      "Engineering, EPC and specialist contractor firms",
      "Tech, SaaS and telecoms businesses",
      "Professional services and consultancies",
      "Holding-company and multi-entity structures",
    ],
    faqs: [
      {
        q: "Can you support a divisional finance team inside a listed group?",
        a: "Yes. We maintain the divisional books, produce the monthly submission pack in the group's format, and liaise with the group finance team on adjustments and consolidation entries.",
      },
      {
        q: "Do you handle 3PL and warehousing revenue and cost recognition?",
        a: "Yes. Storage, handling, transport and value-added service revenue are separated in the ledger and reconciled monthly. Fleet, fuel and third-party carrier costs are matched to the revenue that generated them.",
      },
      {
        q: "How do you handle group reporting deadlines?",
        a: "We agree the close date and submission format up front. Bank reconciliations, supplier bills and VAT-ready GL cleanup are completed inside the group's monthly timetable.",
      },
      {
        q: "Are your fees based on suburb?",
        a: "No. Fees reflect business size and complexity — turnover, transaction volume, employees and VAT status. A Waterfall corporate and a Bedfordview SME with the same profile pay the same fee.",
      },
    ],
    nearby: ["midrand", "woodmead", "sunninghill", "sandton", "rivonia"],
  },
  {
    slug: "fourways",
    name: "Fourways",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Fourways | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Fourways businesses. SAICA registered practice serving Cedar Square, Monte Casino and the William Nicol corridor.",
    h1: "Bookkeeping Services in Fourways",
    intro:
      "We serve businesses in Fourways with remote bookkeeping, payroll, VAT and SARS compliance — from the retailers, restaurants and personal services around Cedar Square and Monte Casino through the owner-managed consultancies, medical practices and contractors across the wider Fourways area.",
    localContext:
      "Fourways is a mixed commercial and residential node with a strong SME base. Retail and hospitality dominate at Cedar Square, Fourways Mall and Monte Casino, while the surrounding office parks house consultancies, IT businesses, medical practices, estate agencies and specialist contractors. Neighbouring Dainfern, Steyn City and Lonehill add a large owner-managed and personal-services clientele.",
    sections: [
      {
        heading: "Bookkeeping for retail, hospitality and SMEs",
        body: "For retail and hospitality clients we reconcile POS or till system sales against bank deposits daily and pull it into monthly VAT-ready books. For consultancies and contractors we work on Xero, Sage or Pastel with bank feeds, supplier bill processing and management reporting each month.",
      },
      {
        heading: "Payroll and SARS compliance",
        body: "Monthly payroll processing, payslips, EMP201, EMP501, IRP5 issuing, UIF and Workmen's Compensation are handled. VAT201, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are filed on time.",
      },
      {
        heading: "Contractors, tradespeople and specialist services",
        body: "Fourways has a strong contractor and specialist-services base — plumbers, electricians, landscapers, home renovators, security firms. We handle project or job costing where useful, retention and deposit tracking, and the payroll and VAT compliance that comes with employing site teams.",
      },
    ],
    businessTypes: [
      "Retailers at Cedar Square, Fourways Mall and Monte Casino",
      "Restaurants, coffee shops and specialty food",
      "Consultancies and boutique advisory firms",
      "Medical, dental and allied practices",
      "Estate agencies and property companies",
      "IT and MSP businesses",
      "Contractors, tradespeople and home-services businesses",
      "Personal services (fitness studios, salons, wellness)",
    ],
    faqs: [
      {
        q: "Can you reconcile POS or till system sales against bank deposits?",
        a: "Yes. For retail and hospitality clients we reconcile daily takings from your POS to the bank, separate cash, card and delivery-app revenue, and give you a clean VAT-ready ledger every month.",
      },
      {
        q: "Do you handle contractor deposits, retentions and progress claims?",
        a: "Yes. Deposits and retentions are held in the correct liability accounts, progress claims are billed and tracked, and revenue is recognised in the correct period.",
      },
      {
        q: "How is a delivery-app business (Uber Eats, Mr D) handled?",
        a: "We reconcile the platform statements to the bank, gross up commission and delivery fees, and treat them correctly for VAT.",
      },
      {
        q: "Do you support catch-up bookkeeping?",
        a: "Yes. Where a Fourways business has fallen behind we quote the catch-up separately, rebuild the accounts and submit the outstanding VAT201s, EMP201s and ITR14s.",
      },
    ],
    nearby: ["bryanston", "sunninghill", "randburg", "midrand", "sandton"],
  },
  {
    slug: "melrose-arch",
    name: "Melrose Arch",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Melrose Arch | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Melrose Arch businesses. SAICA registered practice serving Melrose, Melrose North and the M1 corridor.",
    h1: "Bookkeeping Services in Melrose Arch",
    intro:
      "We serve businesses in Melrose Arch with remote bookkeeping, payroll, VAT and SARS compliance — from professional and financial services firms in the precinct through the hospitality operators, retailers and creative businesses around Melrose Arch's mixed-use blocks.",
    localContext:
      "Melrose Arch is a compact, high-density precinct that mixes corporate offices, residential and hospitality. Tenants include mid-tier legal and financial services firms, tech companies, creative agencies, and a strong hospitality and food and beverage cluster serving both office and residential traffic. Adjacent Melrose and Melrose North add owner-managed professional practices.",
    sections: [
      {
        heading: "Bookkeeping for professional and creative firms",
        body: "Monthly bookkeeping in Xero, Sage or Pastel, with bank feeds, supplier bill capture and project-level reporting where useful. Creative and agency clients get gross margin visibility per client or campaign; professional firms get partner or director reporting.",
      },
      {
        heading: "Hospitality, restaurant and F&B bookkeeping",
        body: "For restaurants and F&B operators we reconcile POS revenue against bank daily, separate cash, card and delivery-app receipts, track cost of sales and stock movement, and produce weekly and monthly management reporting on demand.",
      },
      {
        heading: "Payroll, VAT and tax",
        body: "Monthly payroll, EMP201, EMP501, IRP5 issuing, VAT201 on the SARS cycle, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled centrally.",
      },
    ],
    businessTypes: [
      "Mid-tier legal and financial services firms",
      "Creative agencies, studios and PR",
      "Tech and SaaS companies",
      "Restaurants and F&B operators",
      "Boutique retailers and specialty stores",
      "Property companies and letting agents",
      "Serviced-office and small corporate tenants",
      "Health, wellness and personal services",
    ],
    faqs: [
      {
        q: "Do you handle restaurant weekly reporting?",
        a: "Yes. Where an operator wants weekly food cost, beverage cost and gross margin reporting we set the file up so the numbers can be pulled weekly rather than only at month-end.",
      },
      {
        q: "Can you split revenue by outlet or brand?",
        a: "Yes. Multi-outlet or multi-brand operators get tracking categories or classes per site so gross margin is visible outlet-by-outlet.",
      },
      {
        q: "How do you handle service charges and tips for staff?",
        a: "Service charge and tip pools are held in the correct liability accounts, distributed on the agreed pool basis and taxed correctly through payroll where applicable.",
      },
      {
        q: "Do you support hospitality VAT nuances?",
        a: "Yes. Standard-rated vs. zero-rated supplies, non-deductible entertainment input VAT and the correct treatment of no-shows and service charges are handled on the VAT201.",
      },
    ],
    nearby: ["rosebank", "illovo", "houghton", "sandton", "bedfordview"],
  },
  {
    slug: "rivonia",
    name: "Rivonia",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Rivonia | Payroll, VAT & SARS Support",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Rivonia businesses. SAICA registered practice serving Rivonia Village, Wendywood and Edenburg.",
    h1: "Bookkeeping Services in Rivonia",
    intro:
      "We serve businesses in Rivonia with remote bookkeeping, payroll, VAT and SARS compliance — from the professional firms and consultancies along Rivonia Boulevard through the specialist contractors, showrooms and owner-managed SMEs in Wendywood, Edenburg and Kramerville.",
    localContext:
      "Rivonia is Sandton's less-glossy neighbour and much of the real business happens here — consultancies, IT and engineering firms, medical practices, specialist showrooms, and a strong contractor and trade-services cluster. Kramerville next door hosts design showrooms and specialist retail that adds a further layer of owner-managed clients.",
    sections: [
      {
        heading: "Bookkeeping suited to owner-managed businesses",
        body: "Rivonia clients typically want tight bookkeeping without corporate fee overheads. Monthly work runs on Xero, Sage or Pastel, with bank feeds, supplier bill capture, VAT-ready reconciliation and a monthly management pack. Where a business needs project or contract-level reporting we build it in.",
      },
      {
        heading: "Payroll, VAT and SARS compliance",
        body: "Payroll processing, EMP201 monthly, EMP501 bi-annually, VAT201 on your SARS cycle, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all delivered on time.",
      },
      {
        heading: "Contractors, showrooms and service businesses",
        body: "Rivonia and Kramerville host a lot of showroom, specialist retail and contractor businesses. Inventory management, deposit accounting, retention tracking and project costing are all part of the standard toolkit.",
      },
    ],
    businessTypes: [
      "Consulting and advisory firms",
      "IT, MSP and cybersecurity businesses",
      "Engineering and technical consultancies",
      "Medical and dental practices",
      "Design showrooms and specialist retail (Kramerville)",
      "Contractors, installers and specialist trades",
      "Property companies and letting agents",
      "Small manufacturers and light industrial",
    ],
    faqs: [
      {
        q: "Do you handle inventory-based businesses?",
        a: "Yes. Inventory-tracked accounting on Xero, Sage or Pastel, stock take reconciliations and cost of sales postings are supported. For high-SKU operators we recommend a linked inventory system that pushes journals into the accounting file.",
      },
      {
        q: "Can you set up job or project costing on Xero?",
        a: "Yes. Xero Projects (or tracking categories where Projects is not licensed) lets us report gross margin per job — useful for contractors, engineers and creative teams.",
      },
      {
        q: "Do you handle showroom deposits and layby correctly?",
        a: "Yes. Customer deposits and laybys sit in a liability account until goods are delivered, at which point revenue is recognised and VAT is triggered correctly.",
      },
      {
        q: "Which package covers a typical Rivonia consultancy?",
        a: "Most fit the Medium Level package (turnover under R12m, up to 10 employees plus VAT). Smaller consultancies fit Entry Level (turnover under R2.3m, up to 5 employees).",
      },
    ],
    nearby: ["sandton", "woodmead", "sunninghill", "bryanston", "melrose-arch"],
  },
  {
    slug: "woodmead",
    name: "Woodmead",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Woodmead | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Woodmead businesses. SAICA registered practice serving the N1 office parks and the wider Sunninghill node.",
    h1: "Bookkeeping Services in Woodmead",
    intro:
      "We serve businesses in Woodmead with remote bookkeeping, payroll, VAT and SARS compliance — from the tech, telecoms and distribution head offices in the N1 office parks through the mid-market consultancies and specialist services that fill the surrounding blocks.",
    localContext:
      "Woodmead is a corridor of low-density office parks along the N1 between Sandton and Waterfall. It houses tech, telecoms, distribution and pharmaceutical head offices, together with a strong cluster of mid-market consulting, engineering and IT businesses. The office parks around Woodmead Retail Park and Woodlands Office Park set the tone.",
    sections: [
      {
        heading: "Bookkeeping for tech, telecoms and distribution",
        body: "SaaS revenue recognition, deferred income tracking, foreign-currency processor reconciliations and inventory-based distribution are all standard for Woodmead clients. Monthly bookkeeping runs on Xero, Sage or Pastel with a clean monthly close and management reporting.",
      },
      {
        heading: "Payroll for professional and salaried teams",
        body: "Monthly payroll for salaried teams, directors' remuneration, share-incentive tracking where relevant, EMP201, EMP501, UIF and IRP5 issuing are all included.",
      },
      {
        heading: "VAT, tax and CIPC",
        body: "VAT201 on your SARS cycle, IRP6 provisional tax in August and February, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are handled centrally. Where a SARS verification arises the response and supporting document submission is handled via eFiling.",
      },
    ],
    businessTypes: [
      "Tech and SaaS companies",
      "Telecoms and connectivity businesses",
      "Distribution and wholesale operators",
      "Pharmaceutical and medical device businesses",
      "Engineering and technical consultancies",
      "Mid-market management consultancies",
      "IT and MSP businesses",
      "Corporate divisional teams",
    ],
    faqs: [
      {
        q: "Can you handle SaaS deferred revenue?",
        a: "Yes. Subscription billing, deferred revenue schedules and revenue recognition over the subscription period are supported on Xero and Sage. Where a subscription platform pushes data into the accounting file we handle the reconciliation.",
      },
      {
        q: "Do you handle foreign-currency Stripe or PayPal reconciliation?",
        a: "Yes. Multi-currency processor accounts are reconciled to bank settlements, with FX gains and losses handled per SARS rules.",
      },
      {
        q: "Can you slot into a group monthly reporting timetable?",
        a: "Yes. We agree the close date and submission format up front, run bank recs, supplier bills and VAT-ready GL cleanup on that cycle, and deliver management accounts before the group deadline.",
      },
      {
        q: "Do you handle inventory-based distribution?",
        a: "Yes. Inventory accounting, stock take reconciliations, landed cost calculations and cost of sales postings are supported.",
      },
    ],
    nearby: ["sunninghill", "rivonia", "waterfall", "sandton", "midrand"],
  },
  {
    slug: "sunninghill",
    name: "Sunninghill",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Sunninghill | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Sunninghill businesses. SAICA registered practice serving the Sunninghill office parks and Paulshof.",
    h1: "Bookkeeping Services in Sunninghill",
    intro:
      "We serve businesses in Sunninghill with remote bookkeeping, payroll, VAT and SARS compliance — from the IT, medical and consulting tenants of the Sunninghill office parks through the owner-managed businesses in Paulshof, Chartwell and the surrounding suburbs.",
    localContext:
      "Sunninghill sits between Sandton, Rivonia and Woodmead and is a natural home for owner-managed businesses. IT and telecoms, medical practices, engineering consultancies, specialist recruiters and family businesses fill the office parks along Rivonia Road and Maxwell Drive. Sunninghill Hospital anchors a healthcare cluster of specialists, pathologists and allied practices.",
    sections: [
      {
        heading: "Bookkeeping for owner-managed businesses",
        body: "Monthly bookkeeping runs on Xero, Sage or Pastel with bank feeds, supplier bills, VAT-ready reconciliations and a monthly management pack. For smaller clients we produce a shorter, decision-focused pack that fits the fee bracket.",
      },
      {
        heading: "Payroll and SARS compliance",
        body: "Payroll processing, EMP201 monthly, EMP501 bi-annually, IRP5 issuing, VAT201 on your SARS cycle, IRP6 provisional tax and ITR14 annual returns are all handled. CIPC annual returns and beneficial ownership submissions are kept current.",
      },
      {
        heading: "Medical and specialist practices",
        body: "Sunninghill Hospital's specialist and pathology footprint means a lot of practice bookkeeping. We handle medical aid receipting, practice management software reconciliations, directors' loan accounts and the split between personal and business expenses.",
      },
    ],
    businessTypes: [
      "IT, MSP and cybersecurity businesses",
      "Medical, specialist and pathology practices",
      "Engineering and technical consultancies",
      "Specialist recruitment agencies",
      "Family-owned businesses",
      "Property and letting companies",
      "Contractors and specialist installers",
      "Startups and holding companies",
    ],
    faqs: [
      {
        q: "Do you handle medical practice bookkeeping?",
        a: "Yes. Medical aid receipting, practice management software reconciliations and specialist-practice compliance are all supported.",
      },
      {
        q: "Can you produce a lighter management pack for a small business?",
        a: "Yes. For smaller Entry Level clients we deliver a shorter, decision-focused pack rather than a full corporate-style report.",
      },
      {
        q: "Do you handle SARS objections and appeals?",
        a: "Yes. Registered tax practitioners on the team handle verifications, objections and appeals via eFiling.",
      },
      {
        q: "Are fees fixed monthly?",
        a: "Yes. Each package has a fixed monthly fee that includes all the compliance work in scope, with no per-transaction or per-submission add-ons.",
      },
    ],
    nearby: ["woodmead", "rivonia", "sandton", "bryanston", "waterfall"],
  },
  {
    slug: "bedfordview",
    name: "Bedfordview",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Bedfordview | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Bedfordview businesses. SAICA registered practice serving the East Rand professional and family-business corridor.",
    h1: "Bookkeeping Services in Bedfordview",
    intro:
      "We serve businesses in Bedfordview with remote bookkeeping, payroll, VAT and SARS compliance — from the professional practices around Bradford Road and Kloof Road through the family businesses, medical practices and property companies that make up the East Rand's most established commercial suburb.",
    localContext:
      "Bedfordview is the East Rand's professional-business hub. Legal firms, accountants, financial advisors, medical practices and property companies fill the office nodes around Bedford Centre and along Bradford Road. Family-owned businesses — often multi-generational, with holding-company structures and diversified interests — are the backbone of the local commercial base.",
    sections: [
      {
        heading: "Bookkeeping for professional and family businesses",
        body: "Monthly bookkeeping in Xero, Sage or Pastel. For multi-entity family structures each operating and holding entity gets its own bookkeeping file and compliance, with consolidated management reporting where the owner needs to see the group.",
      },
      {
        heading: "Payroll, VAT and SARS compliance",
        body: "Monthly payroll, EMP201, EMP501, IRP5 issuing, VAT201 on your SARS cycle, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled by a SAICA registered team.",
      },
      {
        heading: "Estate and property companies",
        body: "Bedfordview has a strong property-holding culture. Property-owning companies get monthly bookkeeping, VAT (where the letting is not exempt), CIPC compliance and the tax handling needed for capital gains, share-block or sectional-title interests.",
      },
    ],
    businessTypes: [
      "Legal firms and accounting practices",
      "Medical, dental and specialist practices",
      "Financial advisors and boutique wealth managers",
      "Property-owning and letting companies",
      "Family businesses and holding structures",
      "Retailers around Bedford Centre",
      "Contractors and specialist trades",
      "Restaurants and boutique hospitality",
    ],
    faqs: [
      {
        q: "Do you handle multi-entity family business structures?",
        a: "Yes. Each entity gets its own bookkeeping file and SARS/CIPC compliance, with consolidated reporting where useful for the family or trustees.",
      },
      {
        q: "Do you handle trust bookkeeping and IT12TR submissions?",
        a: "Yes. Trust accounting, distributions to beneficiaries and IT12TR annual trust returns are supported.",
      },
      {
        q: "Can you take over an East Rand set of books mid-year?",
        a: "Yes. Handover, opening balance verification and continuation in your existing software is standard.",
      },
      {
        q: "Do you support both English and Afrikaans reporting?",
        a: "Yes. Correspondence and reporting can be delivered in either language.",
      },
    ],
    nearby: ["houghton", "melrose-arch", "illovo", "sandton", "rosebank"],
  },
  {
    slug: "randburg",
    name: "Randburg",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Randburg | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Randburg businesses. SAICA registered practice serving Ferndale, Bordeaux, Cresta and Northgate.",
    h1: "Bookkeeping Services in Randburg",
    intro:
      "We serve businesses in Randburg with remote bookkeeping, payroll, VAT and SARS compliance — from the SME base in Ferndale, Bordeaux, Windsor and Cresta through the contractors, retailers and personal-services businesses across the wider Randburg area.",
    localContext:
      "Randburg is Johannesburg's largest SME cluster. Contractors and tradespeople, retailers, personal services, specialist repair and installation businesses, and thousands of owner-managed micro-enterprises are based here. The commercial areas around Cresta, Northgate, Ferndale and Bordeaux anchor the local business economy.",
    sections: [
      {
        heading: "Bookkeeping built for SMEs",
        body: "Monthly bookkeeping runs on Xero, Sage or Pastel, with bank feeds, supplier bill capture and a management pack sized to the business. For micro-enterprises the focus is on getting the compliance right and the numbers useful, not on producing a corporate-style report.",
      },
      {
        heading: "Payroll and SARS compliance",
        body: "Payroll processing, payslips, EMP201 monthly, EMP501 bi-annually, IRP5 issuing, VAT201, IRP6 provisional tax and ITR14 annual returns are all included in the standard packages. CIPC annual returns and beneficial ownership submissions are handled centrally.",
      },
      {
        heading: "Catch-up and SARS remediation",
        body: "Many Randburg SMEs find us after their previous bookkeeper stopped submitting. We quote catch-up separately, rebuild the accounts, file the outstanding VAT201s, EMP201s and ITR14s and apply for penalty remission where the grounds justify it.",
      },
    ],
    businessTypes: [
      "Contractors, plumbers, electricians and installers",
      "Retailers and specialist stores",
      "Salons, wellness and personal services",
      "Small manufacturers and workshops",
      "IT and repair businesses",
      "Restaurants and takeaways",
      "Property and letting agents",
      "Owner-managed micro-enterprises",
    ],
    faqs: [
      {
        q: "How affordable are the packages?",
        a: "The Basic package for sole proprietors is R300/month and Entry Level for small companies starts at R2,500/month. Both include the SARS and CIPC compliance most Randburg SMEs need.",
      },
      {
        q: "Can you take on a business that is years behind?",
        a: "Yes. Catch-up is quoted separately and covers rebuilding the books, filing outstanding VAT and PAYE returns, submitting ITR14s and bringing CIPC into good standing.",
      },
      {
        q: "Do you handle deregistered CIPC restorations?",
        a: "Yes. CIPC restoration via CoR 40.5, catching up outstanding annual returns and reactivating the company are supported.",
      },
      {
        q: "Is there a setup fee?",
        a: "No. Onboarding — including Xero, Sage or Pastel setup where required, chart of accounts, bank feeds and payroll setup — is included.",
      },
    ],
    nearby: ["fourways", "bryanston", "northcliff", "parkhurst", "sandton"],
  },
  {
    slug: "illovo",
    name: "Illovo",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Illovo | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Illovo businesses. SAICA registered practice serving Illovo Boulevard, Fricker Road and Chester Road.",
    h1: "Bookkeeping Services in Illovo",
    intro:
      "We serve businesses in Illovo with remote bookkeeping, payroll, VAT and SARS compliance — supporting the boutique financial services firms, legal practices, family offices and consultancies that fill the offices along Illovo Boulevard, Fricker Road and Chester Road.",
    localContext:
      "Illovo is a compact, upmarket professional-services precinct wedged between Rosebank and Sandton. It attracts boutique financial services, wealth managers, family offices, corporate legal and tax advisors, and mid-tier consultancies that want the address without the density of Sandton CBD.",
    sections: [
      {
        heading: "Bookkeeping for boutique financial and legal firms",
        body: "Monthly bookkeeping on Xero, Sage or Pastel, with bank feeds and clean monthly close. For legal firms we handle trust account distinct from business account, section 86(4) transfers and the year-end reporting the legal practice council expects.",
      },
      {
        heading: "Family office and holding-company work",
        body: "Family offices typically run a holding company, several operating and investment entities and one or more trusts. We keep each entity's books separate for SARS purposes and produce consolidated reporting for the family or advisors.",
      },
      {
        heading: "Payroll, VAT and tax",
        body: "Payroll for salaried teams, EMP201, EMP501, IRP5 issuing, VAT201, IRP6 provisional tax, ITR14 annual returns, IT12TR trust returns, CIPC annual returns and beneficial ownership submissions are all handled centrally.",
      },
    ],
    businessTypes: [
      "Boutique financial services and wealth managers",
      "Family offices and holding companies",
      "Corporate legal and tax advisory firms",
      "Mid-tier management consultancies",
      "Executive coaching and specialist advisory practices",
      "Property investment companies",
      "Trusts and estate structures",
      "Small corporate teams and serviced-office tenants",
    ],
    faqs: [
      {
        q: "Do you handle legal trust accounts?",
        a: "Yes. Trust account bookkeeping distinct from business, section 86(4) transfers, monthly reconciliations and the annual reporting the legal practice council expects are supported.",
      },
      {
        q: "Can you handle multiple trusts and companies under one family?",
        a: "Yes. Each trust and company gets its own bookkeeping and SARS/CIPC compliance, with consolidated reporting for the family or advisors.",
      },
      {
        q: "Do you handle IT12TR trust returns and distributions to beneficiaries?",
        a: "Yes. Trust distribution accounting, beneficiary IT3(t) reporting and IT12TR annual trust returns are all supported.",
      },
      {
        q: "Are fees flat monthly?",
        a: "Yes. Each entity has a flat monthly fee based on size and complexity — turnover, transactions, staff, VAT status — with no per-submission add-ons.",
      },
    ],
    nearby: ["rosebank", "melrose-arch", "sandton", "houghton", "bedfordview"],
  },
  {
    slug: "houghton",
    name: "Houghton",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Houghton | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Houghton businesses. SAICA registered practice serving Houghton Estate, Killarney and Saxonwold.",
    h1: "Bookkeeping Services in Houghton",
    intro:
      "We serve businesses in Houghton with remote bookkeeping, payroll, VAT and SARS compliance — from professional practices in Houghton Estate through the family offices, private companies and investment structures based across Killarney and Saxonwold.",
    localContext:
      "Houghton and its neighbours Killarney and Saxonwold are dominated by professional practices, family offices, private investment companies and long-established owner-managed businesses. The Houghton Golf Estate and the surrounding medical corridor add further professional and specialist practices.",
    sections: [
      {
        heading: "Bookkeeping for professional and family businesses",
        body: "Monthly bookkeeping on Xero, Sage or Pastel, with clean monthly close and a management pack that focuses on the decisions the owner or family actually needs to take.",
      },
      {
        heading: "Payroll and SARS compliance",
        body: "Payroll for professional teams, EMP201 monthly, EMP501 bi-annually, VAT201, IRP6 provisional tax, ITR14 annual returns, IT12TR trust returns, CIPC annual returns and beneficial ownership submissions are all handled centrally.",
      },
      {
        heading: "Trust and investment company work",
        body: "Trust bookkeeping, investment company income and expense tracking, dividend and interest income, capital gains events and beneficiary distributions are all handled with the correct SARS treatment.",
      },
    ],
    businessTypes: [
      "Family offices and private investment companies",
      "Medical and specialist practices",
      "Boutique advisory and consulting firms",
      "Property investment and letting companies",
      "Trusts and estate structures",
      "Professional practices (legal, accounting, actuarial)",
      "NPOs and PBOs based in Houghton",
      "Long-established owner-managed businesses",
    ],
    faqs: [
      {
        q: "Do you handle NPO and PBO bookkeeping?",
        a: "Yes. NPO monthly bookkeeping, IT12EI annual returns, section 18A donor reporting and SARS PBO compliance are all supported.",
      },
      {
        q: "Can you handle a family investment company with multiple asset classes?",
        a: "Yes. Listed portfolio income, property, private-company investments and offshore holdings are all tracked with the correct SARS treatment.",
      },
      {
        q: "Do you file IT12TR trust returns?",
        a: "Yes. Trust accounting, distribution schedules and IT12TR annual returns are part of the standard toolkit.",
      },
      {
        q: "Do fees change if the entity is a trust vs. company?",
        a: "Fees reflect the volume and complexity of the entity's transactions, not the legal form.",
      },
    ],
    nearby: ["illovo", "melrose-arch", "rosebank", "bedfordview", "parkhurst"],
  },
  {
    slug: "parkhurst",
    name: "Parkhurst",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Parkhurst | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Parkhurst businesses. SAICA registered practice serving 4th Avenue retail, restaurants and creative studios.",
    h1: "Bookkeeping Services in Parkhurst",
    intro:
      "We serve businesses in Parkhurst with remote bookkeeping, payroll, VAT and SARS compliance — from the boutique retailers, restaurants and coffee shops along 4th Avenue through the creative studios, agencies and independent professionals scattered across the suburb.",
    localContext:
      "Parkhurst's 4th Avenue is one of the most concentrated small-business strips in Johannesburg. Boutique retail, hospitality, wellness and personal services dominate at street level, and the surrounding blocks house creative studios, design agencies, photographers, architects and independent professionals.",
    sections: [
      {
        heading: "Bookkeeping for small retail and hospitality",
        body: "For 4th Avenue retail and hospitality we reconcile POS or till system daily takings against bank deposits, separate cash and card revenue, track cost of sales and produce a monthly VAT-ready ledger. Delivery-app revenue (Uber Eats, Mr D) is reconciled from platform statements.",
      },
      {
        heading: "Creative and agency bookkeeping",
        body: "Xero-based bookkeeping with job or client tracking, gross margin reporting per project, and the payroll and freelancer compliance that comes with creative teams (including 25%-withheld personal service provider work where applicable).",
      },
      {
        heading: "SARS and CIPC compliance",
        body: "VAT201 on your assigned cycle, EMP201 monthly, EMP501 bi-annually, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled.",
      },
    ],
    businessTypes: [
      "Boutique retailers on 4th Avenue",
      "Restaurants, coffee shops and delis",
      "Creative studios and design agencies",
      "Architects, interior designers and stylists",
      "Photographers and production companies",
      "Wellness studios and personal services",
      "Independent professionals and consultants",
      "Owner-managed micro-enterprises",
    ],
    faqs: [
      {
        q: "Do you handle daily POS reconciliation?",
        a: "Yes. Cash, card, EFT and delivery-app revenue are reconciled daily against bank deposits, with a clean VAT-ready GL monthly.",
      },
      {
        q: "Can you handle freelancers and personal service providers on payroll?",
        a: "Yes. PSP payroll (25%-withholding where applicable) and independent contractor payments with SARS-compliant documentation are both handled.",
      },
      {
        q: "Are your fees suitable for a small 4th Avenue business?",
        a: "Yes. The Basic package for sole proprietors starts at R300/month and Entry Level for small companies starts at R2,500/month — both include the compliance most small Parkhurst businesses need.",
      },
      {
        q: "Do you offer catch-up bookkeeping for a small retailer?",
        a: "Yes. Catch-up is quoted separately, covering rebuild of the accounts and filing of outstanding SARS and CIPC returns.",
      },
    ],
    nearby: ["rosebank", "randburg", "northcliff", "illovo", "houghton"],
  },
  {
    slug: "northcliff",
    name: "Northcliff",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Northcliff | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Northcliff businesses. SAICA registered practice serving Northcliff, Blackheath and Fairland.",
    h1: "Bookkeeping Services in Northcliff",
    intro:
      "We serve businesses in Northcliff with remote bookkeeping, payroll, VAT and SARS compliance — supporting the owner-managed businesses, professional practices and contractors based across Northcliff, Blackheath and Fairland.",
    localContext:
      "Northcliff and its neighbouring suburbs Blackheath, Fairland and Cresta host a strong owner-managed business base. Professional practices, IT businesses, contractors, specialist retailers and family-run companies form the bulk of the local business economy.",
    sections: [
      {
        heading: "Bookkeeping for owner-managed businesses",
        body: "Monthly bookkeeping on Xero, Sage or Pastel with bank feeds, supplier bill capture and a decision-focused monthly management pack. For smaller clients we produce a shorter pack sized to the fee bracket.",
      },
      {
        heading: "Payroll, VAT and tax",
        body: "Payroll processing, EMP201 monthly, EMP501 bi-annually, IRP5 issuing, VAT201, IRP6 provisional tax and ITR14 annual returns are all included.",
      },
      {
        heading: "CIPC and beneficial ownership",
        body: "CIPC annual returns, beneficial ownership submissions and where needed CIPC restoration (CoR 40.5) are handled centrally so the company stays in good standing with CIPC and the banks.",
      },
    ],
    businessTypes: [
      "Professional practices (legal, accounting, financial advisory)",
      "IT and MSP businesses",
      "Contractors and specialist trades",
      "Family businesses",
      "Property and letting companies",
      "Specialist retailers",
      "Personal services and wellness",
      "Owner-managed SMEs",
    ],
    faqs: [
      {
        q: "Do you handle CIPC restorations?",
        a: "Yes. CoR 40.5 restoration, catching up outstanding annual returns and reactivating the company are supported.",
      },
      {
        q: "Can you take over from another bookkeeper?",
        a: "Yes. Handover, opening balance verification and continuation in your existing software is standard.",
      },
      {
        q: "Are fees fixed monthly?",
        a: "Yes. Each package has a flat monthly fee with no per-transaction or per-submission add-ons.",
      },
      {
        q: "Do you support Afrikaans-speaking clients?",
        a: "Yes. Correspondence and reporting can be delivered in Afrikaans on request.",
      },
    ],
    nearby: ["randburg", "parkhurst", "fourways", "bryanston", "rosebank"],
  },
];

// ---------------- Durban ----------------
const durbanSuburbs: Suburb[] = [
  {
    slug: "umhlanga",
    name: "Umhlanga",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Umhlanga | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Umhlanga businesses. SAICA registered practice serving Umhlanga Ridge, Umhlanga Village and Gateway.",
    h1: "Bookkeeping Services in Umhlanga",
    intro:
      "We serve businesses in Umhlanga with remote bookkeeping, payroll, VAT and SARS compliance — from the professional and financial services firms in Umhlanga Ridge through the retailers, restaurants and hospitality operators around Gateway, Umhlanga Village and the Promenade.",
    localContext:
      "Umhlanga Ridge is Durban's newest and busiest commercial node. Insurers, banks, medical scheme administrators, BPO operators and mid-tier consultancies fill the towers along Ridge Road, while Gateway anchors a huge retail and F&B cluster. Umhlanga Village adds boutique hospitality and specialist retail.",
    sections: [
      {
        heading: "Bookkeeping for Umhlanga Ridge professional firms",
        body: "Monthly bookkeeping in Xero, Sage or Pastel, with bank feeds, clean monthly close, VAT-ready reconciliations and a management pack that lands before your board or exco meeting.",
      },
      {
        heading: "Retail and hospitality at Gateway and the Village",
        body: "For retail and hospitality we reconcile POS or till system daily takings against bank, separate cash, card and delivery-app revenue, track cost of sales and produce weekly or monthly management reporting on demand.",
      },
      {
        heading: "Payroll, VAT and SARS compliance",
        body: "Monthly payroll, EMP201, EMP501, IRP5 issuing, VAT201 on your SARS cycle, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled centrally.",
      },
    ],
    businessTypes: [
      "Insurers and medical scheme administrators",
      "BPO and call centre operators",
      "Mid-tier consultancies and financial services",
      "Retailers and restaurants at Gateway",
      "Boutique hospitality at Umhlanga Village and the Promenade",
      "Property developers and letting agents",
      "Medical and specialist practices at Umhlanga Hospital",
      "Startups, holding companies and family businesses",
    ],
    faqs: [
      {
        q: "Do you support BPO and call centre payroll?",
        a: "Yes. High-headcount payroll with variable shift, allowance and commission structures is standard. EMP201, EMP501, UIF and Workmen's Compensation are handled.",
      },
      {
        q: "Can you handle multi-outlet Gateway retailers?",
        a: "Yes. Multi-outlet operators get tracking categories per site with gross margin visibility outlet by outlet.",
      },
      {
        q: "Do you handle medical practice bookkeeping in Umhlanga?",
        a: "Yes. Medical aid receipting, practice management software reconciliations and specialist-practice SARS compliance are supported.",
      },
      {
        q: "Do you charge more for Umhlanga clients?",
        a: "No. Fees are set by business size and complexity — turnover, transactions, staff, VAT status — not by suburb.",
      },
    ],
    nearby: ["la-lucia", "durban-north", "mount-edgecombe", "umdloti", "ballito"],
  },
  {
    slug: "la-lucia",
    name: "La Lucia",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services La Lucia | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for La Lucia businesses. SAICA registered practice serving La Lucia Ridge, La Lucia Mall and the M4 corridor.",
    h1: "Bookkeeping Services in La Lucia",
    intro:
      "We serve businesses in La Lucia with remote bookkeeping, payroll, VAT and SARS compliance — supporting the professional services, medical practices, retailers and owner-managed SMEs based across La Lucia Ridge, La Lucia Mall and along the M4 between Umhlanga and Durban North.",
    localContext:
      "La Lucia sits between Umhlanga and Durban North and hosts a strong professional-services base. Legal firms, financial advisors, medical practices, IT and MSP businesses, and family-owned companies dominate the office parks around La Lucia Ridge. La Lucia Mall anchors the neighbourhood retail cluster.",
    sections: [
      {
        heading: "Bookkeeping for professional and medical practices",
        body: "Monthly bookkeeping on Xero, Sage or Pastel. For medical practices we reconcile practice management software revenue against bank receipts monthly and separate personal from business expenses; for professional firms we produce director- or partner-level reporting.",
      },
      {
        heading: "Payroll, VAT and SARS compliance",
        body: "Payroll processing, EMP201 monthly, EMP501 bi-annually, IRP5 issuing, VAT201, IRP6 provisional tax and ITR14 annual returns are all included. CIPC annual returns and beneficial ownership submissions are handled centrally.",
      },
      {
        heading: "SME and family business bookkeeping",
        body: "Family businesses and multi-entity owner-managed structures get separate bookkeeping files per entity with consolidated management reporting where the owner needs to see the group.",
      },
    ],
    businessTypes: [
      "Legal and financial advisory firms",
      "Medical, dental and specialist practices",
      "IT, MSP and cybersecurity businesses",
      "Family-owned businesses and holding structures",
      "Property companies and letting agents",
      "Retailers at La Lucia Mall",
      "Specialist trades and installers",
      "Independent professionals",
    ],
    faqs: [
      {
        q: "Do you handle multi-entity family structures?",
        a: "Yes. Each entity gets its own bookkeeping file and SARS/CIPC compliance, with consolidated reporting where useful.",
      },
      {
        q: "Can you handle a set of medical practice books mid-year?",
        a: "Yes. Handover, opening balance verification and continuation in your existing software (including specialist practice management software) is standard.",
      },
      {
        q: "Do you deal with SARS verifications remotely?",
        a: "Yes. Registered tax practitioners handle VAT and income tax verifications, requests for supporting documents and objections via eFiling.",
      },
      {
        q: "Is there a setup fee?",
        a: "No. Onboarding is included in the monthly fee.",
      },
    ],
    nearby: ["umhlanga", "durban-north", "morningside", "mount-edgecombe", "berea"],
  },
  {
    slug: "durban-north",
    name: "Durban North",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Durban North | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Durban North businesses. SAICA registered practice serving Broadway, Kensington and Glenashley.",
    h1: "Bookkeeping Services in Durban North",
    intro:
      "We serve businesses in Durban North with remote bookkeeping, payroll, VAT and SARS compliance — from the professional and medical practices along Broadway through the retailers, contractors and family businesses across Kensington, Glenashley and the surrounding neighbourhoods.",
    localContext:
      "Durban North is an established residential and business suburb between the M4 and the Umgeni River. Medical practices, legal firms, accounting practices, IT businesses, contractors and family-owned SMEs form the bulk of the local business economy, with retail concentrated along Broadway and Chelsea Drive.",
    sections: [
      {
        heading: "Bookkeeping for professional and owner-managed businesses",
        body: "Monthly bookkeeping on Xero, Sage or Pastel with bank feeds, supplier bill capture and a management pack sized to the business. For smaller clients we produce a shorter, decision-focused pack.",
      },
      {
        heading: "Payroll and SARS compliance",
        body: "Payroll processing, EMP201 monthly, EMP501 bi-annually, IRP5 issuing, VAT201, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled.",
      },
      {
        heading: "Catch-up and SARS remediation",
        body: "A frequent request from Durban North businesses is catch-up bookkeeping after a previous bookkeeper stopped submitting. We quote catch-up separately, rebuild the accounts and file outstanding VAT201s, EMP201s and ITR14s.",
      },
    ],
    businessTypes: [
      "Medical, dental and specialist practices",
      "Legal firms and accounting practices",
      "IT, MSP and specialist businesses",
      "Contractors, plumbers, electricians and installers",
      "Retailers along Broadway",
      "Family businesses and holding structures",
      "Property companies and letting agents",
      "Owner-managed SMEs",
    ],
    faqs: [
      {
        q: "Do you handle CIPC restorations?",
        a: "Yes. CoR 40.5 restoration, catching up outstanding annual returns and reactivating the company are supported.",
      },
      {
        q: "Can you produce Afrikaans-language reporting?",
        a: "Yes. Correspondence and reporting can be delivered in Afrikaans on request.",
      },
      {
        q: "Are your packages affordable for a small Durban North business?",
        a: "Yes. Basic (R300/month for sole proprietors) and Entry Level (from R2,500/month for small companies) cover the compliance most SMEs need.",
      },
      {
        q: "Do you handle SARS objections?",
        a: "Yes. Registered tax practitioners handle verifications, objections and appeals via eFiling.",
      },
    ],
    nearby: ["la-lucia", "morningside", "umhlanga", "berea", "riverhorse-valley"],
  },
  {
    slug: "ballito",
    name: "Ballito",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Ballito | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Ballito businesses. SAICA registered practice serving Ballito, Simbithi, Sheffield Beach and Salt Rock.",
    h1: "Bookkeeping Services in Ballito",
    intro:
      "We serve businesses in Ballito with remote bookkeeping, payroll, VAT and SARS compliance — supporting the property developers, hospitality operators, professional practices and owner-managed businesses that have followed the North Coast semigration boom into Ballito, Simbithi, Sheffield Beach and Salt Rock.",
    localContext:
      "Ballito is the fastest-growing business node on the KZN north coast. Property developers, estate agents, hospitality operators, F&B businesses, professional practices and specialist services have all followed the residential growth around Ballito, Simbithi Estate, Zimbali, Sheffield Beach and Salt Rock. Many of the businesses are relocations from Gauteng and want a proper compliance partner from day one.",
    sections: [
      {
        heading: "Bookkeeping for property, hospitality and professional firms",
        body: "Monthly bookkeeping on Xero, Sage or Pastel. Property developers get project-based accounting per development, hospitality operators get POS-to-bank reconciliation and cost-of-sales reporting, and professional practices get standard monthly close and management reporting.",
      },
      {
        heading: "Payroll and SARS compliance",
        body: "Payroll processing, EMP201, EMP501, IRP5 issuing, VAT201, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled centrally. Nothing needs to be dropped off at a SARS branch.",
      },
      {
        heading: "Semigration and new company setup",
        body: "Many Ballito clients are new companies set up after a relocation. We handle the SARS registrations (income tax, VAT, PAYE, UIF, WCA), CIPC director and beneficial ownership setup, and the initial Xero or Sage file build.",
      },
    ],
    businessTypes: [
      "Property developers and estate agencies",
      "Hospitality operators, hotels and short-stay",
      "Restaurants, cafés and F&B along the Ballito coastline",
      "Professional practices (legal, medical, financial advisory)",
      "Retailers at The Well and Ballito Junction",
      "Contractors and specialist trades",
      "Body corporates and HOAs (Simbithi, Zimbali, Brettenwood)",
      "Owner-managed SMEs relocated from Gauteng",
    ],
    faqs: [
      {
        q: "Do you handle new company setup and SARS registrations?",
        a: "Yes. CIPC registration or restoration, SARS income tax, VAT, PAYE, UIF and WCA registrations, and the initial accounting file setup are all supported.",
      },
      {
        q: "Can you handle a property development project ledger?",
        a: "Yes. Development costs, sales revenue, transfer proceeds and VAT on developer sales are all handled with the correct SARS treatment.",
      },
      {
        q: "Do you support HOA and body corporate bookkeeping?",
        a: "Yes. Levy raising, arrear management, expense capture and monthly trustee reporting are supported.",
      },
      {
        q: "Do you charge more because Ballito is on the coast?",
        a: "No. Fees are set by business size and complexity, not by suburb or region.",
      },
    ],
    nearby: ["umdloti", "mount-edgecombe", "umhlanga", "la-lucia", "durban-north"],
  },
  {
    slug: "mount-edgecombe",
    name: "Mount Edgecombe",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Mount Edgecombe | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Mount Edgecombe businesses. SAICA registered practice serving Mount Edgecombe Business Estate and Cornubia.",
    h1: "Bookkeeping Services in Mount Edgecombe",
    intro:
      "We serve businesses in Mount Edgecombe with remote bookkeeping, payroll, VAT and SARS compliance — supporting the light industrial, logistics, manufacturing and professional tenants of Mount Edgecombe Business Estate, the Riverhorse-adjacent industrial belt and the growing Cornubia commercial corridor.",
    localContext:
      "Mount Edgecombe sits at the top of Umhlanga's commercial belt and hosts a mix of light industrial, logistics, manufacturing and office park tenants. Cornubia's mixed-use precinct adds retail, F&B and office tenants. The proximity to the N2, King Shaka International and Dube TradePort makes it a natural home for import-export and logistics-linked businesses.",
    sections: [
      {
        heading: "Bookkeeping for light industrial and logistics",
        body: "Monthly bookkeeping in Xero, Sage or Pastel, with high-volume creditor processing, inventory or stock-based accounting, and job or route costing where useful. Import-VAT audit trails and forex-invoiced income are both handled.",
      },
      {
        heading: "Payroll for wage and salaried teams",
        body: "Wage payroll with overtime, shift, standby and travel allowances, plus salaried teams and director remuneration, are all processed monthly. EMP201, EMP501, UIF and Workmen's Compensation (Medium Level packages) are included.",
      },
      {
        heading: "VAT, tax and CIPC",
        body: "VAT201 on your SARS cycle, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled. Import-VAT reclaim, zero-rated exports and the correct treatment of freight and customs are supported.",
      },
    ],
    businessTypes: [
      "Light industrial and manufacturing",
      "Logistics, warehousing and 3PL operators",
      "Import-export and distribution businesses",
      "Office park tenants",
      "Retail and F&B in Cornubia",
      "Dube TradePort-adjacent operators",
      "Specialist contractors and installers",
      "Owner-managed SMEs",
    ],
    faqs: [
      {
        q: "Do you handle import VAT reclaim?",
        a: "Yes. Import-VAT audit trails, DA490 clearing agent documentation, valid tax invoice checks and the VAT201 reclaim are all supported.",
      },
      {
        q: "Can you handle zero-rated export VAT?",
        a: "Yes. Zero-rated export documentation, valid tax invoice requirements and the SARS documentary evidence required to defend a zero-rate claim are handled.",
      },
      {
        q: "Do you support Dube TradePort SEZ tenants?",
        a: "Yes. SEZ tax incentives, VAT treatment and reporting are supported where relevant.",
      },
      {
        q: "Do you handle wage payroll?",
        a: "Yes. Weekly, fortnightly or monthly wage payroll with overtime, shift and allowance structures is standard.",
      },
    ],
    nearby: ["umhlanga", "riverhorse-valley", "la-lucia", "ballito", "umdloti"],
  },
  {
    slug: "westville",
    name: "Westville",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Westville | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Westville businesses. SAICA registered practice serving Westway Office Park, Pavilion and Cowies Hill.",
    h1: "Bookkeeping Services in Westville",
    intro:
      "We serve businesses in Westville with remote bookkeeping, payroll, VAT and SARS compliance — from the professional and financial services tenants of Westway Office Park through the retailers, contractors and family businesses around Pavilion and Cowies Hill.",
    localContext:
      "Westville is the Upper Highway's most established commercial node. Westway Office Park hosts financial services, insurers, medical scheme administrators and mid-tier consultancies, while the Pavilion anchors the retail cluster. The surrounding suburbs house professional practices, contractors and family-owned businesses.",
    sections: [
      {
        heading: "Bookkeeping for professional and financial firms",
        body: "Monthly bookkeeping on Xero, Sage or Pastel, with bank feeds, clean monthly close, VAT-ready reconciliations and a management pack. For financial services businesses we handle FSCA-adjacent reporting and reconciliations where required.",
      },
      {
        heading: "Payroll and SARS compliance",
        body: "Payroll for professional teams, EMP201, EMP501, IRP5 issuing, VAT201, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled centrally.",
      },
      {
        heading: "Retail, contractor and family business work",
        body: "For retailers, contractors and family businesses we run standard monthly bookkeeping and produce a decision-focused management pack. Multi-entity family structures get separate bookkeeping per entity with consolidated reporting.",
      },
    ],
    businessTypes: [
      "Financial services and insurers at Westway",
      "Medical scheme administrators",
      "Mid-tier consultancies",
      "Retailers at Pavilion",
      "Professional practices (legal, accounting, financial advisory)",
      "Family businesses and holding structures",
      "Contractors and specialist trades",
      "Owner-managed SMEs",
    ],
    faqs: [
      {
        q: "Do you handle FSP-linked reconciliations?",
        a: "Yes. Where an FSP requires monthly commission reconciliations, deposit tracking and separate business vs. trust accounting we set the file up to support it.",
      },
      {
        q: "Can you handle multi-entity family businesses?",
        a: "Yes. Each entity gets separate bookkeeping and SARS/CIPC compliance, with consolidated reporting where useful.",
      },
      {
        q: "Do you handle catch-up bookkeeping?",
        a: "Yes. Catch-up is quoted separately and covers rebuild of the accounts and submission of outstanding SARS and CIPC returns.",
      },
      {
        q: "Are fees fixed?",
        a: "Yes. Each package has a flat monthly fee with no per-submission add-ons.",
      },
    ],
    nearby: ["pinetown", "kloof", "hillcrest", "berea", "riverhorse-valley"],
  },
  {
    slug: "hillcrest",
    name: "Hillcrest",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Hillcrest | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Hillcrest businesses. SAICA registered practice serving Hillcrest Corner, Heritage Market and Waterfall.",
    h1: "Bookkeeping Services in Hillcrest",
    intro:
      "We serve businesses in Hillcrest with remote bookkeeping, payroll, VAT and SARS compliance — supporting the professional practices, retailers, restaurants and owner-managed businesses across Hillcrest Corner, Heritage Market and the wider Upper Highway.",
    localContext:
      "Hillcrest is the commercial heart of the Upper Highway. Professional practices, medical and specialist practitioners, retailers, restaurants and family businesses fill the nodes around Hillcrest Corner, Heritage Market and along Old Main Road. Neighbouring Waterfall and Kloof add further owner-managed businesses to the local base.",
    sections: [
      {
        heading: "Bookkeeping for professional and owner-managed businesses",
        body: "Monthly bookkeeping on Xero, Sage or Pastel with bank feeds, supplier bill capture and a management pack sized to the business.",
      },
      {
        heading: "Retail and hospitality",
        body: "For retail and hospitality clients we reconcile POS or till system daily takings against bank, separate cash, card and delivery-app revenue, track cost of sales and produce weekly or monthly management reporting.",
      },
      {
        heading: "Payroll, VAT and tax",
        body: "Payroll processing, EMP201 monthly, EMP501 bi-annually, IRP5 issuing, VAT201, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled.",
      },
    ],
    businessTypes: [
      "Professional practices (medical, legal, financial advisory)",
      "Retailers at Hillcrest Corner and Heritage Market",
      "Restaurants, coffee shops and boutique F&B",
      "Estate agencies and property companies",
      "Contractors and specialist trades",
      "Family businesses",
      "Personal services and wellness",
      "Independent professionals",
    ],
    faqs: [
      {
        q: "Do you handle daily POS reconciliation?",
        a: "Yes. Cash, card, EFT and delivery-app revenue are reconciled daily against bank deposits, with a clean VAT-ready GL monthly.",
      },
      {
        q: "Can you produce weekly management reporting for a restaurant?",
        a: "Yes. Weekly food cost, beverage cost and gross margin reporting are supported where the operator wants to run the numbers weekly.",
      },
      {
        q: "Do you handle CIPC restoration?",
        a: "Yes. CoR 40.5 restoration and catch-up of outstanding annual returns are supported.",
      },
      {
        q: "Do you charge more for Upper Highway clients?",
        a: "No. Fees are set by business size and complexity, not by suburb.",
      },
    ],
    nearby: ["kloof", "westville", "pinetown", "durban-north", "la-lucia"],
  },
  {
    slug: "kloof",
    name: "Kloof",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Kloof | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Kloof businesses. SAICA registered practice serving Kloof Village, Krantzkloof and the Upper Highway.",
    h1: "Bookkeeping Services in Kloof",
    intro:
      "We serve businesses in Kloof with remote bookkeeping, payroll, VAT and SARS compliance — supporting the professional practices, medical practitioners, contractors and owner-managed businesses across Kloof, Krantzkloof and the surrounding Upper Highway suburbs.",
    localContext:
      "Kloof is a residential suburb with a strong owner-managed business base. Professional practices, medical practitioners, specialist trades, IT businesses and family-owned companies form the bulk of the local economy, with retail concentrated around Kloof Village.",
    sections: [
      {
        heading: "Bookkeeping for owner-managed businesses",
        body: "Monthly bookkeeping on Xero, Sage or Pastel with bank feeds, supplier bill capture and a decision-focused management pack. For smaller clients we produce a shorter pack sized to the fee bracket.",
      },
      {
        heading: "Payroll and SARS compliance",
        body: "Payroll processing, EMP201 monthly, EMP501 bi-annually, IRP5 issuing, VAT201, IRP6 provisional tax and ITR14 annual returns are all included in the standard packages.",
      },
      {
        heading: "CIPC, beneficial ownership and remediation",
        body: "CIPC annual returns, beneficial ownership submissions and CoR 40.5 restoration where needed are handled centrally.",
      },
    ],
    businessTypes: [
      "Medical and specialist practitioners",
      "Legal firms and financial advisors",
      "IT and MSP businesses",
      "Contractors and specialist trades",
      "Family businesses",
      "Retailers at Kloof Village",
      "Property and letting companies",
      "Owner-managed SMEs",
    ],
    faqs: [
      {
        q: "Are your packages affordable for a small Kloof business?",
        a: "Yes. Basic (R300/month for sole proprietors) and Entry Level (from R2,500/month for small companies) cover the compliance most SMEs need.",
      },
      {
        q: "Do you handle CIPC restorations?",
        a: "Yes. CoR 40.5 restoration, outstanding annual returns and reactivating the company are supported.",
      },
      {
        q: "Can you handle SARS objections?",
        a: "Yes. Registered tax practitioners handle verifications, objections and appeals via eFiling.",
      },
      {
        q: "Do you produce Afrikaans reporting on request?",
        a: "Yes. Correspondence and reporting can be delivered in Afrikaans.",
      },
    ],
    nearby: ["hillcrest", "westville", "pinetown", "durban-north", "berea"],
  },
  {
    slug: "riverhorse-valley",
    name: "Riverhorse Valley",
    priority: "high",
    published: true,
    metaTitle: "Bookkeeping Services Riverhorse Valley | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Riverhorse Valley businesses. SAICA registered practice serving the Riverhorse Valley industrial and logistics estate.",
    h1: "Bookkeeping Services in Riverhorse Valley",
    intro:
      "We serve businesses in Riverhorse Valley with remote bookkeeping, payroll, VAT and SARS compliance — supporting the light industrial, logistics, distribution and manufacturing tenants of the Riverhorse Valley Business Estate and the surrounding N2-adjacent industrial belt.",
    localContext:
      "Riverhorse Valley is Durban's newest and largest industrial and logistics estate. Distribution, warehousing, 3PL, light manufacturing and assembly, and specialist logistics businesses fill the estate. Its position at the N2/M19 interchange and proximity to the port and King Shaka International make it a natural home for import-export and cross-border logistics.",
    sections: [
      {
        heading: "Bookkeeping for logistics and light industrial",
        body: "Monthly bookkeeping in Xero, Sage or Pastel with high-volume creditor processing, inventory or stock-based accounting, fleet cost tracking and job or route costing where useful.",
      },
      {
        heading: "Payroll for wage and salaried teams",
        body: "Wage payroll with overtime, shift, standby, travel and site allowances, plus salaried teams and director remuneration, are all processed monthly. EMP201, EMP501, UIF and Workmen's Compensation are included.",
      },
      {
        heading: "Import VAT, export zero-rating and SARS",
        body: "Import-VAT reclaim (with clearing agent documentation), zero-rated export handling with correct SARS documentary evidence, and standard VAT201, IRP6, ITR14 and CIPC compliance are all handled.",
      },
    ],
    businessTypes: [
      "Distribution and warehousing operators",
      "3PL and cross-border logistics",
      "Light manufacturing and assembly",
      "Import-export businesses",
      "Cold chain and specialised logistics",
      "Freight forwarders and clearing agents",
      "Specialist industrial contractors",
      "Fleet and transport operators",
    ],
    faqs: [
      {
        q: "Do you handle import VAT reclaim?",
        a: "Yes. Import-VAT audit trails, DA490 clearing agent documentation and the VAT201 reclaim are all supported.",
      },
      {
        q: "Can you handle zero-rated export VAT?",
        a: "Yes. Zero-rated export documentation and the SARS documentary evidence required to defend a zero-rate claim are handled.",
      },
      {
        q: "Do you handle fleet cost tracking?",
        a: "Yes. Fuel, maintenance, tyres, licences and tolls per vehicle or route, reconciled against fleet card statements, are supported.",
      },
      {
        q: "Do you handle wage payroll for site teams?",
        a: "Yes. Weekly, fortnightly and monthly wage payroll with overtime, shift, standby and site allowances is standard.",
      },
    ],
    nearby: ["springfield-park", "mount-edgecombe", "umhlanga", "westville", "durban-north"],
  },
  {
    slug: "umdloti",
    name: "Umdloti",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Umdloti | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Umdloti businesses. SAICA registered practice serving Umdloti Beach, Selection Beach and the M4 north corridor.",
    h1: "Bookkeeping Services in Umdloti",
    intro:
      "We serve businesses in Umdloti with remote bookkeeping, payroll, VAT and SARS compliance — supporting the guesthouses, short-stay operators, hospitality businesses, retailers and owner-managed SMEs across Umdloti Beach, Selection Beach and the wider North Coast.",
    localContext:
      "Umdloti is a small coastal village between Umhlanga and Ballito with a growing hospitality, short-stay and small-business economy. Guesthouses, boutique hotels, Airbnb operators, restaurants, retailers and specialist services form the bulk of the local business base.",
    sections: [
      {
        heading: "Bookkeeping for hospitality and short-stay operators",
        body: "For hospitality and short-stay operators we reconcile platform revenue (Airbnb, Booking.com, Nightsbridge, Lekkeslaap) against bank, separate booking and service revenue, track cost of sales for F&B where applicable, and produce a VAT-ready ledger monthly.",
      },
      {
        heading: "Payroll and SARS compliance",
        body: "Payroll processing, EMP201, EMP501, IRP5 issuing, VAT201, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled centrally.",
      },
      {
        heading: "Small business and owner-managed work",
        body: "Retailers, specialist services and owner-managed SMEs get standard monthly bookkeeping with a decision-focused management pack.",
      },
    ],
    businessTypes: [
      "Guesthouses and boutique hotels",
      "Airbnb and short-stay operators",
      "Restaurants and cafés",
      "Beachfront retailers and specialty stores",
      "Property companies and letting agents",
      "Contractors and specialist trades",
      "Personal services and wellness",
      "Owner-managed SMEs",
    ],
    faqs: [
      {
        q: "Do you handle Airbnb and Booking.com reconciliation?",
        a: "Yes. Platform statements are reconciled against bank settlements, with commission, cleaning fees and service charges handled correctly for VAT and income tax.",
      },
      {
        q: "When is a guesthouse liable for VAT?",
        a: "Once taxable turnover exceeds R1m in a 12-month rolling window a business must register for VAT. From April 2026 the compulsory threshold rises to R2.3m. We handle both the registration and the subsequent VAT201 cycle.",
      },
      {
        q: "Can you handle a small guesthouse's payroll?",
        a: "Yes. Payroll for small hospitality teams with variable hours, allowances and tips is supported.",
      },
      {
        q: "Do you charge more for coastal clients?",
        a: "No. Fees are set by business size and complexity, not by suburb.",
      },
    ],
    nearby: ["ballito", "mount-edgecombe", "umhlanga", "la-lucia", "durban-north"],
  },
  {
    slug: "morningside",
    name: "Morningside",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Morningside Durban | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Morningside Durban businesses. SAICA registered practice serving Windermere Road, Florida Road and Musgrave.",
    h1: "Bookkeeping Services in Morningside, Durban",
    intro:
      "We serve businesses in Morningside, Durban with remote bookkeeping, payroll, VAT and SARS compliance — from the professional practices around Windermere Road through the restaurants, retailers and creative studios that fill Florida Road and the wider Berea corridor.",
    localContext:
      "Morningside is an established Durban business and residential suburb. Legal firms, accounting practices, medical practitioners, financial advisors and creative studios fill the offices around Windermere Road, while Florida Road anchors one of Durban's strongest restaurant and boutique-retail strips.",
    sections: [
      {
        heading: "Bookkeeping for professional practices",
        body: "Monthly bookkeeping on Xero, Sage or Pastel, with bank feeds, supplier bill capture and a management pack for partners or directors.",
      },
      {
        heading: "Restaurant and boutique retail on Florida Road",
        body: "For F&B and boutique retail we reconcile POS or till system revenue against bank, separate cash, card and delivery-app receipts, track cost of sales and produce weekly or monthly management reporting on demand.",
      },
      {
        heading: "Payroll, VAT and tax",
        body: "Payroll processing, EMP201 monthly, EMP501 bi-annually, IRP5 issuing, VAT201, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns and beneficial ownership submissions are all handled centrally.",
      },
    ],
    businessTypes: [
      "Legal firms, accounting practices and financial advisors",
      "Medical, dental and specialist practitioners",
      "Restaurants, coffee shops and boutique F&B on Florida Road",
      "Creative studios and design agencies",
      "Boutique retailers",
      "Property companies and letting agents",
      "Personal services and wellness",
      "Independent professionals",
    ],
    faqs: [
      {
        q: "Do you handle Florida Road restaurant reporting?",
        a: "Yes. Weekly food cost, beverage cost and gross margin reporting are supported where the operator wants to run the numbers weekly.",
      },
      {
        q: "Can you handle delivery-app revenue for a restaurant?",
        a: "Yes. Uber Eats, Mr D and other platform statements are reconciled to bank, with commission and delivery fees grossed up correctly for VAT.",
      },
      {
        q: "Do you take over from another Durban bookkeeper mid-year?",
        a: "Yes. Handover, opening balance verification and continuation in your existing software is standard.",
      },
      {
        q: "Are fees fixed?",
        a: "Yes. Each package has a flat monthly fee with no per-submission add-ons.",
      },
    ],
    nearby: ["berea", "durban-north", "la-lucia", "umhlanga", "westville"],
  },
  {
    slug: "berea",
    name: "Berea",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Berea Durban | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Berea Durban businesses. SAICA registered practice serving Musgrave, Overport and the Berea Ridge.",
    h1: "Bookkeeping Services in Berea, Durban",
    intro:
      "We serve businesses on the Berea, Durban with remote bookkeeping, payroll, VAT and SARS compliance — supporting the professional practices, medical practitioners, retailers and family businesses across Musgrave, Overport and the wider Berea Ridge.",
    localContext:
      "The Berea is one of Durban's oldest business and professional suburbs. Legal firms, accounting practices, medical and specialist practitioners, and family-owned businesses dominate the office nodes around Musgrave Road, Berea Road and along the Overport corridor. The Berea Centre and Musgrave Centre anchor the retail cluster.",
    sections: [
      {
        heading: "Bookkeeping for professional and family businesses",
        body: "Monthly bookkeeping on Xero, Sage or Pastel with bank feeds, supplier bill capture and a management pack sized to the business. Multi-entity family structures get separate bookkeeping per entity with consolidated reporting.",
      },
      {
        heading: "Payroll and SARS compliance",
        body: "Payroll processing, EMP201 monthly, EMP501 bi-annually, IRP5 issuing, VAT201, IRP6 provisional tax and ITR14 annual returns are all included.",
      },
      {
        heading: "CIPC, trust and estate work",
        body: "CIPC annual returns, beneficial ownership submissions, IT12TR trust returns and trust distribution accounting are all handled centrally.",
      },
    ],
    businessTypes: [
      "Legal firms and accounting practices",
      "Medical and specialist practitioners",
      "Family businesses and holding structures",
      "Trusts and estate structures",
      "Retailers at Berea Centre and Musgrave Centre",
      "Property and letting companies",
      "Contractors and specialist trades",
      "Independent professionals",
    ],
    faqs: [
      {
        q: "Do you handle trust bookkeeping and IT12TR returns?",
        a: "Yes. Trust accounting, distributions to beneficiaries and IT12TR annual returns are supported.",
      },
      {
        q: "Can you handle multi-entity family businesses?",
        a: "Yes. Each entity gets its own bookkeeping and SARS/CIPC compliance, with consolidated reporting where useful.",
      },
      {
        q: "Do you handle catch-up bookkeeping?",
        a: "Yes. Catch-up is quoted separately, with rebuild of the accounts and submission of outstanding SARS and CIPC returns.",
      },
      {
        q: "Do you support Afrikaans reporting?",
        a: "Yes. Correspondence and reporting can be delivered in Afrikaans on request.",
      },
    ],
    nearby: ["morningside", "durban-north", "westville", "pinetown", "la-lucia"],
  },
  {
    slug: "pinetown",
    name: "Pinetown",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Pinetown | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Pinetown businesses. SAICA registered practice serving Pinetown CBD, New Germany and Westmead industrial.",
    h1: "Bookkeeping Services in Pinetown",
    intro:
      "We serve businesses in Pinetown with remote bookkeeping, payroll, VAT and SARS compliance — supporting the manufacturers, distributors, contractors, retailers and owner-managed SMEs across Pinetown CBD, New Germany and the Westmead industrial belt.",
    localContext:
      "Pinetown is one of KZN's oldest industrial and commercial nodes. Light manufacturing, distribution, engineering, contracting and specialist services dominate the industrial belt around Westmead and New Germany, while the Pinetown CBD hosts a strong retail and professional-services cluster.",
    sections: [
      {
        heading: "Bookkeeping for manufacturing and distribution",
        body: "Monthly bookkeeping on Xero, Sage or Pastel with high-volume creditor processing, inventory or stock-based accounting, cost of goods manufactured schedules and job costing where useful.",
      },
      {
        heading: "Payroll for wage and salaried teams",
        body: "Wage payroll with overtime, shift, standby and site allowances, plus salaried teams and director remuneration, are all processed monthly. EMP201, EMP501, UIF and Workmen's Compensation are included.",
      },
      {
        heading: "VAT, tax, CIPC and remediation",
        body: "VAT201, IRP6 provisional tax, ITR14 annual returns, CIPC annual returns, beneficial ownership submissions and where needed CIPC restoration and catch-up bookkeeping are all handled.",
      },
    ],
    businessTypes: [
      "Light manufacturers and assemblers",
      "Distribution and wholesale businesses",
      "Engineering and technical contractors",
      "Warehousing and logistics",
      "Retailers in Pinetown CBD",
      "Professional practices",
      "Family businesses and holding structures",
      "Owner-managed SMEs",
    ],
    faqs: [
      {
        q: "Do you handle wage payroll for factory or workshop teams?",
        a: "Yes. Weekly, fortnightly and monthly wage payroll with overtime, shift and allowance structures is standard.",
      },
      {
        q: "Can you handle manufacturing inventory and cost of goods?",
        a: "Yes. Raw material, work in progress and finished goods inventory, plus cost of goods manufactured schedules, are supported.",
      },
      {
        q: "Do you handle CIPC restorations?",
        a: "Yes. CoR 40.5 restoration, outstanding annual returns and reactivating the company are supported.",
      },
      {
        q: "Are your packages suitable for a small Pinetown workshop?",
        a: "Yes. Entry Level (from R2,500/month) covers most small workshops and family businesses.",
      },
    ],
    nearby: ["westville", "kloof", "hillcrest", "riverhorse-valley", "springfield-park"],
  },
  {
    slug: "springfield-park",
    name: "Springfield Park",
    priority: "standard",
    published: true,
    metaTitle: "Bookkeeping Services Springfield Park | Payroll, VAT & SARS",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Springfield Park businesses. SAICA registered practice serving the Springfield industrial and retail belt.",
    h1: "Bookkeeping Services in Springfield Park",
    intro:
      "We serve businesses in Springfield Park with remote bookkeeping, payroll, VAT and SARS compliance — supporting the light industrial, distribution, retail and warehousing tenants of the Springfield Park industrial and Value Centre retail belt.",
    localContext:
      "Springfield Park sits on the M19 corridor north of the Durban CBD and hosts a busy mix of light industrial, distribution, warehousing and big-box retail. The Springfield Value Centre and surrounding retail parks anchor the retail cluster, while the industrial belt houses distributors, importers and light manufacturers.",
    sections: [
      {
        heading: "Bookkeeping for industrial and distribution businesses",
        body: "Monthly bookkeeping on Xero, Sage or Pastel with high-volume creditor processing, inventory or stock-based accounting, and fleet cost tracking where useful.",
      },
      {
        heading: "Retail bookkeeping",
        body: "For retail tenants we reconcile POS or till system daily takings against bank, separate cash, card and layby revenue, track cost of sales and stock movement, and produce a monthly VAT-ready ledger.",
      },
      {
        heading: "Payroll, VAT and SARS compliance",
        body: "Wage and salaried payroll, EMP201, EMP501, UIF, Workmen's Compensation, VAT201 (including import VAT reclaim where relevant), IRP6, ITR14, CIPC annual returns and beneficial ownership submissions are all handled.",
      },
    ],
    businessTypes: [
      "Distribution and wholesale",
      "Import-export businesses",
      "Light manufacturers",
      "Warehousing and 3PL",
      "Big-box retailers at Springfield Value Centre",
      "Specialist retailers",
      "Fleet and transport operators",
      "Specialist industrial contractors",
    ],
    faqs: [
      {
        q: "Do you handle import VAT reclaim?",
        a: "Yes. Import-VAT audit trails, DA490 documentation and the VAT201 reclaim are supported.",
      },
      {
        q: "Can you handle high-volume creditor processing?",
        a: "Yes. Supplier invoices are captured through an inbox that pushes them into the accounting file with data extraction and approvals inside the software.",
      },
      {
        q: "Do you handle POS reconciliation for a big-box retailer?",
        a: "Yes. Daily POS takings against bank, cost of sales and stock movement are all handled.",
      },
      {
        q: "Are fees fixed?",
        a: "Yes. Each package has a flat monthly fee with no per-submission add-ons.",
      },
    ],
    nearby: ["riverhorse-valley", "berea", "morningside", "pinetown", "westville"],
  },
];

export const cities: City[] = [
  {
    slug: "cape-town",
    name: "Cape Town",
    province: "Western Cape",
    metaTitle: "Bookkeeping Services Cape Town | Payroll, VAT & SARS Support",
    metaDescription:
      "Remote bookkeeping, payroll, VAT and SARS compliance for Cape Town businesses. Serving CBD, Century City, Claremont, Constantia, Sea Point and the Northern Suburbs.",
    h1: "Bookkeeping Services in Cape Town",
    intro:
      "Bookkeeping Services South Africa provides remote bookkeeping, payroll, tax and compliance support to businesses operating across Cape Town — from the CBD and Foreshore through the Southern Suburbs, Atlantic Seaboard and out to the Tyger Valley and Winelands.",
    overview:
      "Cape Town has one of the most diverse business economies in South Africa. Tech and financial services cluster around Century City and the Foreshore, professional and medical practices operate throughout Claremont, Newlands and Constantia, tourism and hospitality drive the Atlantic Seaboard, and manufacturing, retail and logistics run through Bellville, Durbanville and the Northern Suburbs. Every one of these businesses shares the same monthly compliance obligations to SARS and CIPC, and that is where our remote practice fits in.",
    economy:
      "We work with Cape Town clients using Xero, Sage, Pastel and other cloud accounting systems. Because everything is done digitally — bank feeds, payroll approvals, SARS eFiling and CIPC submissions — there is no need for site visits or courier runs. Your monthly management pack, VAT201, EMP201, EMP501, UIF declarations, provisional tax, annual tax returns, annual financial statements and CIPC annual returns are all handled by a SAICA registered team that already understands the reporting rhythm SARS and CIPC expect.",
    businessTypes: [
      "Professional service firms (legal, advisory, consulting)",
      "Medical and dental practices",
      "Property companies, letting agents and body corporates",
      "Restaurants, cafés and boutique hospitality",
      "Tech, SaaS and creative studios",
      "Retail, e-commerce and wholesale traders",
      "Manufacturers, importers and light industrial businesses",
      "Contractors, tradespeople and specialist services",
      "Startups and growing owner-managed companies",
    ],
    faqs: [
      {
        q: "Do you have an office in Cape Town?",
        a: "No — our practice is fully remote. That is a deliberate choice: it means Cape Town businesses get a SAICA registered team without paying for office overheads baked into their monthly fee, and every submission to SARS or CIPC happens electronically anyway.",
      },
      {
        q: "Which parts of Cape Town do you serve?",
        a: "Every business area — the CBD, Foreshore, V&A Waterfront, Sea Point, Green Point, Century City, Claremont, Newlands, Constantia, Tyger Valley, Durbanville, Bellville, Somerset West and Stellenbosch, plus the surrounding Winelands and West Coast towns.",
      },
      {
        q: "How do you handle document collection without meeting in person?",
        a: "Bank feeds pull transactions straight into your accounting system, payroll inputs and invoices come across email or a shared drive, and SARS documents flow through eFiling. We book a monthly video call to walk through the numbers so nothing gets lost in translation.",
      },
      {
        q: "Can you take over from an existing Cape Town bookkeeper?",
        a: "Yes. We handle the handover, request the trial balance and supporting schedules, verify the opening balances and continue in whichever system you already use. There is no charge to migrate between Xero, Sage and Pastel if it makes sense to move.",
      },
      {
        q: "Do your fees change if I am based in the Southern Suburbs vs. the CBD?",
        a: "No. Our packages are priced on the size and complexity of the business — turnover, transaction volume, number of employees — not on the suburb. A Claremont dental practice and a Century City startup with the same profile pay the same fee.",
      },
    ],
    suburbs: capeTownSuburbs,
  },
  {
    slug: "johannesburg",
    name: "Johannesburg",
    province: "Gauteng",
    metaTitle: "Bookkeeping Services Johannesburg | SARS, Payroll & Tax",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Johannesburg businesses. Sandton, Rosebank, Midrand, Waterfall, Bryanston, Bedfordview and surrounds.",
    h1: "Bookkeeping Services in Johannesburg",
    intro:
      "We provide remote bookkeeping, payroll, tax and compliance support to businesses operating throughout Johannesburg — from the Sandton and Rosebank commercial corridors up through Midrand and Waterfall, out to Fourways, Bryanston and Randburg, and across to Bedfordview in the east.",
    overview:
      "Johannesburg is the country's commercial engine, and the businesses inside it are as varied as the city itself: multinational-facing consultancies in Sandton and Melrose Arch, professional firms in Rosebank and Illovo, logistics and light industrial operators along the M1 and around Waterfall, medical practices scattered across the northern suburbs, and thousands of owner-managed SMEs that need proper monthly numbers without hiring a full finance team.",
    economy:
      "Our remote model suits Johannesburg particularly well. Traffic and time pressure make in-person bookkeeping meetings expensive to attend — a Sandton to Randburg round-trip can eat half a morning. Everything we do runs through Xero, Sage, Pastel or your chosen cloud accounting system, with SARS eFiling and CIPC as the compliance backbone. Monthly management accounts, VAT201, EMP201, EMP501, UIF, IRP6 provisional tax, ITR14 annual returns, annual financial statements and CIPC annual returns all come out on time, every time.",
    businessTypes: [
      "Consultancies and professional services firms",
      "Property companies, developers and estate agencies",
      "Medical, dental and specialist practices",
      "Legal practices and advocates' chambers",
      "Retailers, franchises and e-commerce sellers",
      "Logistics, courier and light industrial businesses",
      "Technology companies, agencies and studios",
      "Contractors, engineers and construction firms",
      "Restaurants, coffee shops and hospitality operators",
      "Startups, holding companies and family businesses",
    ],
    faqs: [
      {
        q: "Are you based in Johannesburg?",
        a: "Our practice is fully remote and serves Johannesburg businesses digitally. There is no local branch, and there does not need to be — SARS eFiling and CIPC are national systems, and your accounting software is accessible from anywhere.",
      },
      {
        q: "Which parts of Johannesburg do you cover?",
        a: "Every major business node — Sandton, Rosebank, Bryanston, Midrand, Waterfall, Fourways, Melrose Arch, Rivonia, Woodmead, Sunninghill, Bedfordview, Randburg, Illovo, Houghton, Parkhurst and Northcliff — as well as the wider East Rand and West Rand.",
      },
      {
        q: "How do we get documents to you?",
        a: "Bank feeds go straight from your bank into your accounting software. Invoices, supplier bills and payroll inputs come via email or a shared folder. Nothing needs to be printed or delivered physically, and nothing sits in a courier van waiting to be captured.",
      },
      {
        q: "Can you help with SARS audits or verifications for a Johannesburg business?",
        a: "Yes. Our registered tax practitioners handle SARS verifications, requests for supporting documents, objections and appeals through eFiling. Everything happens electronically, so it does not matter that we are not physically at your office.",
      },
      {
        q: "Do you work with both small and larger Johannesburg companies?",
        a: "Yes. Our Entry Level package suits businesses under R2.3m turnover with up to five employees, and our Medium Level package covers up to R12m turnover with up to ten employees plus VAT. Above that we quote a tailored monthly retainer.",
      },
    ],
    suburbs: jhbSuburbs,
  },
  {
    slug: "durban",
    name: "Durban",
    province: "KwaZulu-Natal",
    metaTitle: "Bookkeeping Services Durban | Payroll, Tax & Compliance",
    metaDescription:
      "Remote bookkeeping, payroll and SARS compliance for Durban and KZN businesses. Umhlanga, La Lucia, Durban North, Ballito, Westville, Hillcrest and Kloof.",
    h1: "Bookkeeping Services in Durban",
    intro:
      "We provide remote bookkeeping, payroll, tax and compliance support to businesses operating in Durban and the wider KwaZulu-Natal north coast — from Umhlanga, La Lucia and Durban North up to Ballito, across to Mount Edgecombe and Riverhorse Valley, and inland through Westville, Hillcrest and Kloof.",
    overview:
      "The Durban economy is anchored by three engines: the port and logistics corridor through Riverhorse Valley and Springfield, the commercial and professional services cluster along the M4 in Umhlanga, La Lucia and Durban North, and the growing residential-business hubs of the Upper Highway (Westville, Hillcrest, Kloof) and North Coast (Ballito, Umdloti, Mount Edgecombe). We work with owner-managed businesses across all of these areas.",
    economy:
      "Everything is delivered remotely through Xero, Sage, Pastel or another cloud accounting system. That matters in Durban because businesses are spread across a long coastal ribbon — Ballito to the CBD is over an hour in traffic. Removing in-person meetings from the workflow keeps your monthly fee predictable and your books current. We handle VAT201, EMP201, EMP501, UIF, provisional tax, annual tax returns, annual financial statements, CIPC annual returns and beneficial ownership submissions on your behalf, and file everything electronically with SARS and CIPC.",
    businessTypes: [
      "Professional services and consulting firms",
      "Property developers, estate agencies and body corporates",
      "Medical and specialist practices",
      "Retailers, restaurants and hospitality operators",
      "Logistics, warehousing and freight businesses",
      "Manufacturers and light industrial operators",
      "Contractors and construction companies",
      "Tourism, guesthouses and short-term rental operators",
      "Startups and owner-managed SMEs",
    ],
    faqs: [
      {
        q: "Are you physically based in Durban?",
        a: "No. Our practice is fully remote and works with Durban businesses through cloud accounting software, email and video calls. It keeps overheads out of your monthly fee and there is no travel time.",
      },
      {
        q: "Which parts of Durban do you serve?",
        a: "Every major business node in and around the city — Umhlanga, La Lucia, Durban North, Ballito, Mount Edgecombe, Westville, Hillcrest, Kloof, Riverhorse Valley, Umdloti, Morningside, Berea, Pinetown and Springfield Park.",
      },
      {
        q: "Do you handle payroll for KZN businesses?",
        a: "Yes. We process monthly payroll, issue payslips, submit EMP201 monthly, reconcile EMP501 bi-annually, and file UIF declarations. Workmen's Compensation returns are included in our Medium Level package.",
      },
      {
        q: "Can you take over books that are years behind?",
        a: "Yes. Catch-up bookkeeping is a common request from Durban businesses whose previous bookkeeper stopped submitting. We quote separately for the backlog, rebuild the accounts, file the outstanding SARS returns and bring the CIPC annual returns up to date.",
      },
      {
        q: "Do you charge more for Ballito or Upper Highway clients?",
        a: "No. Pricing is set by business size and complexity, not by suburb. A Ballito guesthouse and a Kloof consultancy with the same profile pay the same monthly fee.",
      },
    ],
    suburbs: durbanSuburbs,
  },
];

export const getCityBySlug = (slug: string) => cities.find((c) => c.slug === slug);

export const getSuburb = (citySlug: string, suburbSlug: string) => {
  const city = getCityBySlug(citySlug);
  if (!city) return null;
  const suburb = city.suburbs.find((s) => s.slug === suburbSlug);
  if (!suburb) return null;
  return { city, suburb };
};

export const allPublishedAreaPaths = (): string[] => {
  const paths: string[] = [];
  for (const c of cities) {
    paths.push(`/areas/${c.slug}`);
    for (const s of c.suburbs) {
      if (s.published) paths.push(`/areas/${c.slug}/${s.slug}`);
    }
  }
  return paths;
};
